import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Pencil, SearchX, SquarePen, Trash2, X } from 'lucide-react';
import { HelpTip } from '../components/HelpTip';
import { api, ApiCallError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import {
  useAdminUsers,
  useSettingsActivityCodes,
  useSettingsEmployees,
  useSettingsEquipment,
  useSettingsForemanPlaceholders,
  useSettingsMaterials,
  useSettingsPcos,
  useSettingsProject,
  useSettingsUnknowns,
} from '../lib/useSettings';
import { useWeeklyRollups } from '../lib/useRollups';
import {
  ActionMenu,
  Container,
  Dropdown,
  EmptyState,
  ExpandChevron,
  Toggle,
  type ActionMenuItem,
} from '../components/design';
import { NewPcoModal } from '../components/NewPcoModal';
import { RateComparisonModal } from '../components/RateComparisonModal';
import { NewProjectWizard } from '../components/NewProjectWizard';
import { PdfPreviewModal } from '../components/PdfPreviewModal';
import { ConfirmModal } from '../components/ConfirmModal';
import { MoneyInput } from '../components/MoneyInput';
import { useToast } from '../components/ToastProvider';
import { formatDateShort, formatMoneyCents, formatPcoNumber, gcName } from '../lib/format';
import { diffProjectPatch } from '../lib/projectPatchDiff';
import {
  APPLY_RATES_WEEK_LIST_MAX,
  applyRatesCountsLine,
  applyRatesDeltaLine,
  applyRatesNothingText,
  applyRatesResultToast,
  applyRatesSentBeforeLine,
  applyRatesUnmatchedLine,
  applyRatesWeekKey,
  applyRatesWeekLine,
} from '../lib/applyRatesCopy';
import {
  filterEmployees,
  filterEquipment,
  noMatchText,
  rosterCountsLine,
  withOpenEdits,
  type RosterSearchKind,
} from '../lib/settingsSearch';
import {
  MAX_GC_ADDITIONAL_EMAILS,
  type AdminUser,
  type ApplyRatesToSavedKind,
  type ApplyRatesToSavedResponse,
  type Daypart,
  type EmployeeWriteRequest,
  type EquipmentWriteRequest,
  type MaterialWriteRequest,
  type PcoBillingType,
  type PcoWriteRequest,
  type ProjectPatchRequest,
  type SettingsActivityCode,
  type SettingsEmployee,
  type SettingsEquipment,
  type SettingsForemanPlaceholder,
  type SettingsMaterial,
  type SettingsPco,
  type SettingsProject,
  type SettingsUnknownsResponse,
  type UserRole,
} from '@coffman-tm/shared';

/**
 * Distinct, sorted classification strings across the current employee list.
 * Powers the classification dropdown — no separate master-list table needed;
 * any newly-typed value auto-appears in future dropdowns once that employee
 * is saved.
 */
function distinctClassifications(employees: ReadonlyArray<SettingsEmployee>): string[] {
  const set = new Set<string>();
  for (const e of employees) {
    const c = e.classification.trim();
    if (c) set.add(c);
  }
  return [...set].sort();
}

/* ---------------------------------------------------------------------------
 * Small row-level UI helpers shared across the Employees / Equipment /
 * Materials / PCOs tables. Pulled out once here so the four tables read
 * the same; tweak in one place to retone all four.
 * ------------------------------------------------------------------------ */

/**
 * Boolean status indicator for Active / Per-diem cells. Green check when
 * true, muted dash when false. Reads more cleanly than the old dot/dash
 * pair, especially across many rows.
 */
function ActiveCheck({ active }: { active: boolean }) {
  // Wrapper keeps the icon centered in the cell on both axes regardless
  // of cell width — the bare <Check> SVG sometimes sat against the left
  // edge in wide table columns even with text-align: center on the td.
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
        minWidth: 16,
      }}
    >
      {active ? (
        <Check size={16} strokeWidth={2.5} color="var(--positive)" aria-label="Yes" />
      ) : (
        <span style={{ color: 'var(--ink-4)' }} aria-label="No">
          —
        </span>
      )}
    </span>
  );
}

/**
 * Icon button for the inline Edit action on roster/PCO rows.
 * `label` is the accessible name; pass the row identity (e.g. "Edit
 * Bryce Ludlow") so screen readers + native tooltips disambiguate which
 * row the click belongs to.
 */
function EditButton({
  onClick,
  disabled,
  label = 'Edit',
}: {
  onClick: () => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      className="icon-btn"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <Pencil size={14} aria-hidden />
    </button>
  );
}

/**
 * Icon button for the inline Remove action. Same shape as `EditButton`
 * but with the `.danger` modifier so hover tints red.
 */
function RemoveButton({
  onClick,
  disabled,
  label = 'Remove',
}: {
  onClick: () => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      className="icon-btn danger"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <Trash2 size={14} aria-hidden />
    </button>
  );
}

/**
 * Project Settings — tabbed control center (rebuilt 2026-05-14).
 *
 * Five tabs: Project Details / PCOs / Employees / Equipment / Materials.
 * Activity codes live inside the PCOs tab (parent/child relationship). The
 * old "Needs attention" callout that bundled three kinds of unknowns is
 * split per-tab via `<UnknownsSection kind=...>`; each tab surfaces only
 * its own unresolved items. Tab badges (warning-dotted counts) draw the
 * eye to tabs with pending work.
 *
 * URL hash drives the active tab (#project / #pcos / #employees /
 * #equipment / #materials) so deep links and the browser back button
 * work. No URL state for v1 beyond the hash.
 */

type SettingsTab = 'project' | 'pcos' | 'employees' | 'equipment' | 'materials' | 'users';

const TAB_HASH: Record<SettingsTab, string> = {
  project: '#project',
  pcos: '#pcos',
  employees: '#employees',
  equipment: '#equipment',
  materials: '#materials',
  users: '#users',
};

function tabFromHash(hash: string): SettingsTab {
  switch (hash) {
    case '#pcos':
      return 'pcos';
    case '#employees':
      return 'employees';
    case '#equipment':
      return 'equipment';
    case '#materials':
      return 'materials';
    case '#users':
      return 'users';
    case '#project':
    default:
      return 'project';
  }
}

export function SettingsPage() {
  const project = useSettingsProject();
  const unknowns = useSettingsUnknowns();
  const placeholders = useSettingsForemanPlaceholders();
  const employees = useSettingsEmployees();
  const equipment = useSettingsEquipment();
  const materials = useSettingsMaterials();
  const pcos = useSettingsPcos();
  const codes = useSettingsActivityCodes();
  const rollups = useWeeklyRollups();
  const { me } = useAuth();

  // Creating a project is an account-level action, not a project-level one —
  // gate it on the user's role, not their membership on this project.
  const isAdmin = me?.user.role === 'admin';
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const equipmentReviewNote = readEquipmentReviewNote(project.data);

  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = tabFromHash(location.hash);
  const setTab = (t: SettingsTab) =>
    navigate({ hash: TAB_HASH[t] }, { replace: true });

  // Activity-code reassignment triggers a rollup recompute server-side;
  // bring rollups back into sync after any change so PDF Inbox + PCO pages
  // reflect the new mapping.
  const refetchAfterRemap = async () => {
    await Promise.all([codes.refetch(), unknowns.refetch(), rollups.refetch()]);
  };

  const unknownEmpCount = unknowns.data?.employees.length ?? 0;
  const unknownEquipCount = unknowns.data?.equipment.length ?? 0;
  const unknownCodeCount = unknowns.data?.activity_codes.length ?? 0;

  // The IGNORE list splits the codes payload in two. One source, one refetch —
  // an ignored code renders in exactly one of the two sections, never both,
  // and never in the unmapped-codes warning count (the server drops it there).
  const ignoredCodes = (codes.data ?? []).filter((c) => c.ignored_at !== null);
  const mappableCodes = (codes.data ?? []).filter((c) => c.ignored_at === null);

  return (
    <Container>
      <div className="page-head">
        <div>
          <h1 className="page-title">Project Settings</h1>
          <p className="page-sub">
            {project.data
              ? `${project.data.name} · Project ${project.data.project_number} · GC: ${project.data.gc_name}`
              : 'Loading project…'}
          </p>
        </div>
        {isAdmin && project.data && (
          <div className="page-head-actions">
            <button
              type="button"
              className="btn btn-cta"
              onClick={() => setNewProjectOpen(true)}
            >
              New Project
            </button>
          </div>
        )}
      </div>

      <div className="tabs" role="tablist" aria-label="Settings sections">
        <TabButton
          active={activeTab === 'project'}
          onClick={() => setTab('project')}
        >
          Project Details
        </TabButton>
        <TabButton
          active={activeTab === 'pcos'}
          onClick={() => setTab('pcos')}
          badge={unknownCodeCount || undefined}
        >
          PCOs
        </TabButton>
        <TabButton
          active={activeTab === 'employees'}
          onClick={() => setTab('employees')}
          badge={unknownEmpCount || undefined}
        >
          Employees
        </TabButton>
        <TabButton
          active={activeTab === 'equipment'}
          onClick={() => setTab('equipment')}
          badge={unknownEquipCount || undefined}
        >
          Equipment
        </TabButton>
        <TabButton
          active={activeTab === 'materials'}
          onClick={() => setTab('materials')}
        >
          Materials
        </TabButton>
        {/* Hiding the button is a courtesy; the PANEL BODY below is the gate,
            and the API is the real one. Typing #users must not render it. */}
        {isAdmin && (
          <TabButton active={activeTab === 'users'} onClick={() => setTab('users')}>
            Users
          </TabButton>
        )}
      </div>

      {/* ===== Project Details ===== */}
      {activeTab === 'project' && (
        <SettingsSection title="Project Details">
          {project.data ? (
            <ProjectFields project={project.data} onSaved={project.refetch} />
          ) : (
            <Loading />
          )}
        </SettingsSection>
      )}

      {/* ===== PCOs (+ Activity code mappings + unmapped-codes callout) ===== */}
      {activeTab === 'pcos' && (
        <>
          {unknownCodeCount > 0 && unknowns.data && (
            <UnknownsSection
              kind="activity_codes"
              data={unknowns.data}
              pcos={pcos.data ?? []}
              onResolved={refetchAfterRemap}
              onEmployeeAdded={() =>
                Promise.all([employees.refetch(), unknowns.refetch(), rollups.refetch()])
              }
              onEquipmentAdded={() =>
                Promise.all([equipment.refetch(), unknowns.refetch(), rollups.refetch()])
              }
              knownClassifications={distinctClassifications(employees.data ?? [])}
              project={project.data ?? undefined}
              collapsible
              defaultExpanded={false}
            />
          )}
          <SettingsSection
            title={pcos.data ? `PCOs (${pcos.data.length})` : 'PCOs'}
            collapsible
            defaultExpanded={false}
          >
            {pcos.data ? (
              <PcosTable rows={pcos.data} onChanged={pcos.refetch} />
            ) : (
              <Loading />
            )}
          </SettingsSection>
          <SettingsSection
            title="Activity code mappings"
            tipTitle="What this list is"
            tip="Every code the parser has seen on a PDF, mapped to its PCO. New codes appear here with no PCO until someone assigns one."
          >
            {codes.data && pcos.data ? (
              // Ignored codes are excluded — they can't take a mapping (the
              // server 409s), so a row here offering one would be a control
              // that never works. They live in "Ignored codes" below.
              <ActivityCodesTable
                rows={mappableCodes}
                pcos={pcos.data}
                onChanged={refetchAfterRemap}
              />
            ) : (
              <Loading />
            )}
          </SettingsSection>
          {ignoredCodes.length > 0 && (
            <IgnoredCodesSection rows={ignoredCodes} onChanged={refetchAfterRemap} />
          )}
        </>
      )}

      {/* ===== Employees ===== */}
      {activeTab === 'employees' && (
        <>
          {unknownEmpCount > 0 && unknowns.data && (
            <UnknownsSection
              kind="employees"
              data={unknowns.data}
              pcos={pcos.data ?? []}
              onResolved={refetchAfterRemap}
              onEmployeeAdded={() =>
                Promise.all([employees.refetch(), unknowns.refetch(), rollups.refetch()])
              }
              onEquipmentAdded={() =>
                Promise.all([equipment.refetch(), unknowns.refetch(), rollups.refetch()])
              }
              knownClassifications={distinctClassifications(employees.data ?? [])}
              project={project.data ?? undefined}
            />
          )}
          {/* Hidden entirely on a job that has remembered nothing — which is
              every job but the one foreman whose reports print crew slots. */}
          {(placeholders.data?.length ?? 0) > 0 && (
            <RememberedPlaceholdersSection
              rows={placeholders.data ?? []}
              onChanged={() => Promise.all([placeholders.refetch()])}
            />
          )}
          <SettingsSection
            title={
              employees.data ? `Employees (${employees.data.length})` : 'Employees'
            }
            tipTitle="When a rate change takes effect"
            tip="Rate and per-diem changes here apply to future PDFs only. Blocks already saved through the PDF Inbox keep their save-time rates. The parser links a PDF's labor rows to this roster by HeavyJob ID."
          >
            {employees.data && project.data ? (
              <>
                <MasterRateRow
                  project={project.data}
                  employeeCount={employees.data.length}
                  onApplied={employees.refetch}
                  onSavedDefaults={project.refetch}
                />
                <EmployeesTable
                  rows={employees.data}
                  onChanged={async () => {
                    await Promise.all([
                      employees.refetch(),
                      unknowns.refetch(),
                      rollups.refetch(),
                    ]);
                  }}
                  project={project.data}
                  knownClassifications={distinctClassifications(employees.data)}
                />
              </>
            ) : (
              <Loading />
            )}
          </SettingsSection>
        </>
      )}

      {/* ===== Equipment ===== */}
      {activeTab === 'equipment' && (
        <>
          {equipmentReviewNote && (
            <EquipmentRatesReviewBanner
              note={equipmentReviewNote}
              onDismissed={project.refetch}
            />
          )}
          {unknownEquipCount > 0 && unknowns.data && (
            <UnknownsSection
              kind="equipment"
              data={unknowns.data}
              pcos={pcos.data ?? []}
              onResolved={refetchAfterRemap}
              onEmployeeAdded={() =>
                Promise.all([employees.refetch(), unknowns.refetch(), rollups.refetch()])
              }
              onEquipmentAdded={() =>
                Promise.all([equipment.refetch(), unknowns.refetch(), rollups.refetch()])
              }
              knownClassifications={distinctClassifications(employees.data ?? [])}
              project={project.data ?? undefined}
            />
          )}
          <SettingsSection
            title={
              equipment.data ? `Equipment (${equipment.data.length})` : 'Equipment'
            }
            tipTitle="When a rate change takes effect"
            tip="Rate changes here apply to future PDFs only. Blocks already saved through the PDF Inbox keep their save-time rates. The parser links a PDF's equipment rows to this list by Asset ID."
          >
            {equipment.data ? (
              <EquipmentTable rows={equipment.data} onChanged={equipment.refetch} />
            ) : (
              <Loading />
            )}
          </SettingsSection>
        </>
      )}

      {/* ===== Materials ===== */}
      {activeTab === 'materials' && (
        <SettingsSection
          title={
            materials.data
              ? `Materials price list (${materials.data.length})`
              : 'Materials price list'
          }
          tipTitle="Where these prices land"
          tip="Every material line in the portal starts from this list: picking an item pre-fills its default rate, and parser-extracted lines are confirmed against it in the PDF Inbox."
        >
          {materials.data ? (
            <MaterialsTable rows={materials.data} onChanged={materials.refetch} />
          ) : (
            <Loading />
          )}
        </SettingsSection>
      )}

      {/* ===== Users (admin only — the BODY is gated, not just the button) ===== */}
      {activeTab === 'users' &&
        (isAdmin ? (
          <UsersPanel />
        ) : (
          <SettingsSection
            title="Users"
            desc="Adding people and changing who can do what is an administrator action."
          >
            <p className="muted" style={{ fontSize: 13 }}>
              You don&rsquo;t have access to user administration. Ask an administrator on this
              account if you need someone added or removed.
            </p>
          </SettingsSection>
        ))}

      {newProjectOpen && project.data && (
        <NewProjectWizard
          currentProject={project.data}
          onClose={() => setNewProjectOpen(false)}
        />
      )}
    </Container>
  );
}

/**
 * The wizard stamps `projects.equipment_rates_review_note` when it copies
 * equipment across, and Settings → Equipment carries it until dismissed.
 */
function readEquipmentReviewNote(project: SettingsProject | null): string | null {
  const note = project?.equipment_rates_review_note;
  return typeof note === 'string' && note.trim() !== '' ? note.trim() : null;
}

/**
 * "These rates came from another job." Same slim amber strip as
 * StaleDataBanner — nothing is broken, the numbers just can't be trusted yet.
 *
 * Dismissal is a server write, not local state: the note is the only record
 * that the rates were never verified, so it has to survive a reload and be
 * gone for everyone once someone has actually done the pass.
 */
function EquipmentRatesReviewBanner({
  note,
  onDismissed,
}: {
  note: string;
  onDismissed: () => Promise<unknown>;
}) {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dismiss = async () => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      await api.settings.dismissEquipmentReviewNote(token);
      await onDismissed();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not dismiss');
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        padding: '8px 12px',
        marginBottom: 12,
        background: 'var(--surface-warm)',
        border: '1px solid var(--rule-strong)',
        borderLeft: '3px solid var(--warning)',
      }}
    >
      <span aria-hidden style={{ color: 'var(--warning)', flexShrink: 0 }}>
        ⚠
      </span>
      <span style={{ fontSize: 13, lineHeight: 1.35, color: 'var(--ink-2)', flex: 1, minWidth: 200 }}>
        {note} — equipment rates differ per job. Verify every rate below against this
        job&rsquo;s contract before the first rollup goes out.
      </span>
      {err && <Err>{err}</Err>}
      <button type="button" className="btn-link" onClick={dismiss} disabled={busy}>
        {busy ? 'Dismissing…' : 'Dismiss'}
      </button>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  badge,
  children,
}: {
  active: boolean;
  onClick: () => void;
  /** Number to display alongside the tab label as a warning-colored alert.
   *  Omit (or pass 0/undefined) for tabs without pending work. */
  badge?: number;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={active ? 'active' : ''}
      onClick={onClick}
    >
      {children}
      {typeof badge === 'number' && badge > 0 && (
        <span className="tab-badge" aria-label={`${badge} pending`}>
          {badge}
        </span>
      )}
    </button>
  );
}

/* ===========================================================================
 * Section shell + loading
 * ======================================================================== */

function SettingsSection({
  title,
  desc,
  tip,
  tipTitle,
  collapsible = false,
  defaultExpanded = true,
  children,
}: {
  title: string;
  /** Optional: a section whose body explains itself needs no standing blurb,
   *  and an empty `.ss-desc` is better left unrendered than left blank. */
  desc?: ReactNode;
  /** Optional question-mark tooltip rendered inline with the title. Use for
   *  section-wide scope/behavior cues (e.g. "changes apply to future PDFs
   *  only"). Keep the copy to one or two sentences. */
  tip?: ReactNode;
  /** Short noun phrase naming what `tip` is about — the bubble's lead line. */
  tipTitle?: string;
  /** When true, the header becomes a click/keyboard toggle (chevron +
   *  title only) and the desc + body hide until expanded. Same disclosure
   *  pattern as the collapsible funnel sections on PCO/Rollup detail
   *  (ExpandChevron + clickable header) — used here for long tables (PCOs,
   *  60+ rows) so Joey isn't forced to scroll past them. */
  collapsible?: boolean;
  /** Initial expanded state; only meaningful when collapsible. */
  defaultExpanded?: boolean;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const showBody = collapsible ? expanded : true;

  if (!collapsible) {
    return (
      <div className="settings-section">
        <div className="ss-head">
          <div className="ss-title">
            {title}
            {tip && (
              <HelpTip title={tipTitle} learnMore="settings">
                {tip}
              </HelpTip>
            )}
          </div>
          {desc != null && <div className="ss-desc">{desc}</div>}
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="settings-section">
      <div
        className="ss-head"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        title={expanded ? 'Click to collapse' : 'Click to expand'}
        style={{ cursor: 'pointer', userSelect: 'none', ...(expanded ? {} : { marginBottom: 0 }) }}
      >
        <div className="ss-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>
            <ExpandChevron expanded={expanded} size={14} />
          </span>
          <span>
            {title}
            {tip && (
              <HelpTip title={tipTitle} learnMore="settings">
                {tip}
              </HelpTip>
            )}
          </span>
        </div>
        {expanded && desc != null && <div className="ss-desc">{desc}</div>}
      </div>
      {showBody && children}
    </div>
  );
}

/**
 * "Remembered placeholders" — the crew-slot decisions this job repeats.
 *
 * One foreman's reports print crew slots (`LABORER-ID11`, `OPER-IDPF`) in the
 * labor rows instead of people. The PM decides once on a reassign in the PDF
 * Inbox and the portal repeats it. This list is the other half of that
 * promise: a decision the portal keeps applying has to be readable and
 * revocable, or "nothing is learned silently" is not true.
 *
 * Read-only apart from Forget — decisions are MADE where the evidence is, on
 * the line, with the report one click away. Nothing here can create one.
 */
function RememberedPlaceholdersSection({
  rows,
  onChanged,
}: {
  rows: SettingsForemanPlaceholder[];
  onChanged: () => Promise<unknown>;
}) {
  const { token } = useAuth();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  const forget = async (row: SettingsForemanPlaceholder) => {
    if (!token) return;
    setBusyId(row.id);
    try {
      await api.settings.forgetForemanPlaceholder(token, row.id);
      await onChanged();
      toast.push({
        variant: 'success',
        title: `Forgot ${row.placeholder_id}`,
        body: `New reports carrying that ID will come in unassigned again. Lines already filled keep the person they were filled with.`,
      });
    } catch (e) {
      toast.push({
        variant: 'error',
        title: "Couldn't forget that",
        body: e instanceof Error ? e.message : 'Try again, and contact support if it keeps happening.',
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SettingsSection
      title={`Remembered placeholders (${rows.length})`}
      desc="Crew IDs the portal fills in by itself, because you told it who they are."
      tipTitle="Where these come from"
      tip="Some reports print a crew slot (LABORER-ID11) where a name should be. Tick “Remember this” when you reassign one of those lines in the PDF Inbox, and every later report from that foreman fills the same person in — always showing what the report printed. Reassign the line again to change it; Forget here stops it happening on new reports and leaves lines already filled alone."
    >
      <table className="table">
        <thead>
          <tr>
            <th>Foreman</th>
            <th>ID on the report</th>
            <th>Fills in as</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.foreman_name.trim() || `Foreman ${r.foreman_hj_id}`}</td>
              <td className="mono">{r.placeholder_id}</td>
              <td>
                {/* An inactive or missing target is INERT: intake leaves the
                    line unknown rather than billing at a rate nobody holds.
                    Saying so here is the difference between a stale row and a
                    row the PM thinks is working. */}
                {r.employee_name === null ? (
                  <span className="muted">
                    <span className="mono">{r.heavyjob_employee_id}</span> — not on this
                    job&rsquo;s roster, so nothing is filled in
                  </span>
                ) : r.employee_active ? (
                  r.employee_name
                ) : (
                  <span className="muted">
                    {r.employee_name} — inactive, so nothing is filled in
                  </span>
                )}
              </td>
              <td className="num">
                <button
                  type="button"
                  className="btn-quiet"
                  onClick={() => void forget(r)}
                  disabled={busyId !== null}
                  title="Stop filling this ID in on new reports"
                >
                  {busyId === r.id ? 'Forgetting…' : 'Forget'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SettingsSection>
  );
}

function Loading() {
  return (
    <p className="muted" style={{ fontSize: 13 }}>
      Loading…
    </p>
  );
}

function Err({ children }: { children: ReactNode }) {
  return (
    <span style={{ color: 'var(--danger)', fontSize: 12, marginRight: 8 }}>{children}</span>
  );
}

/* ===========================================================================
 * Unknowns surface
 * ======================================================================== */

/**
 * Per-tab "Needs attention" surface. The component used to bundle all
 * three kinds of unknowns (employees / equipment / activity codes) in
 * one stacked card. Since the 2026-05-14 tab rebuild, each Settings tab
 * surfaces only its own unresolved items via `kind`.
 */
function UnknownsSection({
  kind,
  data,
  pcos,
  onResolved,
  onEmployeeAdded,
  onEquipmentAdded,
  knownClassifications,
  project,
  collapsible = false,
  defaultExpanded = true,
}: {
  kind: 'employees' | 'equipment' | 'activity_codes';
  data: SettingsUnknownsResponse;
  pcos: SettingsPco[];
  onResolved: () => Promise<unknown>;
  onEmployeeAdded: () => Promise<unknown>;
  onEquipmentAdded: () => Promise<unknown>;
  knownClassifications: ReadonlyArray<string>;
  /** Project defaults so Add-to-roster prefills rate + per-diem. */
  project?: SettingsProject;
  /** When true, the header becomes a click/keyboard toggle and the desc +
   *  table hide until expanded. Passed from the PCOs tab, where the
   *  unmapped-code backlog can run long; the Employees/Equipment tabs don't
   *  pass this, so those unknowns stay open as before. */
  collapsible?: boolean;
  /** Initial expanded state; only meaningful when collapsible. */
  defaultExpanded?: boolean;
}) {
  const { token } = useAuth();
  const toast = useToast();
  // Both hold an unknown row's `row_key` (raw HJ id / asset id, or `name:<n>`
  // when the parser captured no id) — NOT the name. Two assets can share a
  // description and two workers can share a name; keying the open form by name
  // would expand both twins at once and prefill the wrong id.
  const [emp, setEmp] = useState<string | null>(null); // employee row being added
  const [equip, setEquip] = useState<string | null>(null); // equipment row being added
  const [previewPdfId, setPreviewPdfId] = useState<string | null>(null);
  /**
   * The unmapped activity code Joey is creating a new PCO for. When set,
   * NewPcoModal opens prefilled with the code's PDF description, and on
   * save we auto-assign the code to the new PCO (eliminates the context
   * switch the direction doc §5 calls out).
   */
  const [newPcoForCode, setNewPcoForCode] = useState<{
    codeId: string;
    code: string;
    description: string | null;
  } | null>(null);
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const showBody = collapsible ? expanded : true;

  /**
   * Put an activity code on the IGNORE list. Confirmed, because it is the one
   * option in that dropdown that rewrites blocks the PM can already see: every
   * still-pending block under the code leaves the inbox in the same call.
   * Fully reversible from "Ignored codes", which the confirm says out loud.
   */
  const ignoreCode = async (codeId: string, code: string) => {
    if (!token) return;
    const ok = window.confirm(
      `Ignore activity code ${code}?\n\n` +
        'Work under this code will stop arriving in the PDF Inbox, and any blocks ' +
        'for it still waiting there will be cleared out. Nothing already saved or ' +
        'billed is touched.\n\n' +
        'You can undo this any time from "Ignored codes" on this tab.',
    );
    if (!ok) return;
    try {
      const result = await api.settings.setActivityCodeIgnored(token, codeId, true);
      toast.push({
        variant: 'success',
        title: `${code} ignored`,
        body:
          result.blocks_changed > 0
            ? `${result.blocks_changed} waiting block${result.blocks_changed === 1 ? '' : 's'} cleared from the PDF Inbox.`
            : 'Future PDFs carrying this code will skip the inbox.',
      });
      await onResolved();
    } catch (e) {
      toast.push({
        variant: 'warning',
        title: `Couldn't ignore ${code}`,
        body: e instanceof Error ? e.message : 'Please try again.',
      });
    }
  };

  const count =
    kind === 'employees'
      ? data.employees.length
      : kind === 'equipment'
        ? data.equipment.length
        : data.activity_codes.length;

  // Sublines became tooltips (Brandon 2026-08-05); the employees copy drops
  // its per-diem mention — not every project has one.
  const headerCopy: Record<typeof kind, { title: string; tip: string }> = {
    employees: {
      title: 'Unknown employees',
      tip: "These names came through on foreman PDFs but aren't on the roster. Add each one so future rollups pick up the right rate.",
    },
    equipment: {
      title: 'Unknown equipment',
      tip: "These asset IDs came through on foreman PDFs but aren't in the equipment list. Add each so future rollups carry the right hourly rate.",
    },
    activity_codes: {
      title: 'Activity codes without a PCO',
      tip: "The parser saw these codes on PDFs but they aren't mapped to a PCO yet. Assign each so the rollup math routes correctly.",
    },
  };

  const titleContent = (
    <>
      <span style={{ color: 'var(--warning)' }}>⚠ </span>
      {headerCopy[kind].title}
      <span className="mono" style={{ color: 'var(--warning)', marginLeft: 8, fontSize: 14 }}>
        ({count})
      </span>
      <HelpTip title={headerCopy[kind].title} learnMore="settings">
        {headerCopy[kind].tip}
      </HelpTip>
    </>
  );

  return (
    <div className="settings-section" style={{ borderColor: 'var(--warning)' }}>
      {collapsible ? (
        <div
          className="ss-head"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpanded((v) => !v);
            }
          }}
          title={expanded ? 'Click to collapse' : 'Click to expand'}
          style={{
            cursor: 'pointer',
            userSelect: 'none',
            ...(expanded ? {} : { marginBottom: 0 }),
          }}
        >
          <div className="ss-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--warning)', display: 'inline-flex' }}>
              <ExpandChevron expanded={expanded} size={14} />
            </span>
            <span>{titleContent}</span>
          </div>
        </div>
      ) : (
        <div className="ss-head">
          <div className="ss-title">{titleContent}</div>
        </div>
      )}

      {showBody && (
        <>
        {kind === 'employees' && data.employees.length > 0 && (
        <>
          <div className="eyebrow" style={{ marginTop: 8, marginBottom: 8 }}>
            Unknown employees ({data.employees.length})
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Emp ID</th>
                <th>Name as written</th>
                <th className="num">Occurrences</th>
                <th>First seen</th>
                <th>Last seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.employees.map((e) => (
                <Fragment key={e.row_key}>
                  <tr>
                    <td className="mono">{e.heavyjob_employee_id_raw ?? '—'}</td>
                    <td className="name serif">{e.employee_name_raw}</td>
                    <td className="num">{e.occurrences}</td>
                    <td className="muted">{formatDateShort(e.first_seen)}</td>
                    <td className="muted">{formatDateShort(e.last_seen)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                        {e.latest_daily_pdf_id && (
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => setPreviewPdfId(e.latest_daily_pdf_id)}
                          >
                            View PDF <ArrowRight size={13} aria-hidden />
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-quiet"
                          onClick={() => setEmp(emp === e.row_key ? null : e.row_key)}
                        >
                          {emp === e.row_key ? 'Cancel' : 'Add to roster'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {emp === e.row_key && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0 }}>
                        <EmployeeForm
                          initial={{
                            heavyjob_employee_id: e.heavyjob_employee_id_raw ?? '',
                            name: e.employee_name_raw,
                            classification: '',
                            st_rate_cents: project?.default_st_rate_cents ?? 0,
                            per_diem_eligible: project?.default_per_diem_eligible ?? false,
                            active: true,
                          }}
                          project={project}
                          knownClassifications={knownClassifications}
                          onCancel={() => setEmp(null)}
                          onSubmit={async (body) => {
                            if (!token) return;
                            await api.settings.createEmployee(token, body);
                            setEmp(null);
                            await onEmployeeAdded();
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </>
      )}

      {kind === 'equipment' && data.equipment.length > 0 && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Asset ID</th>
                <th>As written</th>
                <th className="num">Occurrences</th>
                <th>First seen</th>
                <th>Last seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.equipment.map((e) => (
                <Fragment key={e.row_key}>
                  <tr>
                    <td className="mono">{e.asset_id_raw ?? '—'}</td>
                    <td className="name">{e.equipment_name_raw}</td>
                    <td className="num">{e.occurrences}</td>
                    <td className="muted">{formatDateShort(e.first_seen)}</td>
                    <td className="muted">{formatDateShort(e.last_seen)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                        {e.latest_daily_pdf_id && (
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => setPreviewPdfId(e.latest_daily_pdf_id)}
                          >
                            View PDF <ArrowRight size={13} aria-hidden />
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-quiet"
                          onClick={() => setEquip(equip === e.row_key ? null : e.row_key)}
                        >
                          {equip === e.row_key ? 'Cancel' : 'Add equipment'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {equip === e.row_key && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0 }}>
                        <EquipmentForm
                          initial={{
                            asset_id: e.asset_id_raw ?? e.equipment_name_raw,
                            description: e.equipment_name_raw,
                            type: 'rental',
                            hourly_rate_cents: 0,
                            active: true,
                          }}
                          onCancel={() => setEquip(null)}
                          onSubmit={async (body) => {
                            if (!token) return;
                            await api.settings.createEquipment(token, body);
                            setEquip(null);
                            await onEquipmentAdded();
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </>
      )}

      {kind === 'activity_codes' && data.activity_codes.length > 0 && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description from PDF</th>
                <th>Assign to PCO</th>
                <th>First seen</th>
                <th>Last seen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.activity_codes.map((c) => (
                <ActivityRemapRow
                  key={c.id}
                  codeId={c.id}
                  code={c.code}
                  description={c.description_from_pdf}
                  firstSeen={c.first_seen_at}
                  lastSeen={c.last_seen_at}
                  pcos={pcos}
                  currentPcoId={null}
                  onChanged={onResolved}
                  latestDailyPdfId={c.latest_daily_pdf_id}
                  onPreviewPdf={setPreviewPdfId}
                  onCreateNewPco={() =>
                    setNewPcoForCode({
                      codeId: c.id,
                      code: c.code,
                      description: c.description_from_pdf,
                    })
                  }
                  onIgnore={() => ignoreCode(c.id, c.code)}
                />
              ))}
            </tbody>
          </table>
        </>
      )}
        </>
      )}

      <PdfPreviewModal source={previewPdfId} onClose={() => setPreviewPdfId(null)} />
      <NewPcoModal
        open={newPcoForCode !== null}
        onClose={() => setNewPcoForCode(null)}
        // No chooser here: this modal opens FROM an unmapped activity code,
        // and an activity code exists because a foreman ticketed work under
        // it — i.e. weekly rollup work by definition. Making the PM answer
        // "which billing world?" when the answer is already known would be a
        // question with one right answer.
        billingType="weekly_rollup"
        initial={
          newPcoForCode
            ? {
                pco_number: '',
                description: newPcoForCode.description ?? '',
                status: 'active',
              }
            : undefined
        }
        onCreated={async (newPco) => {
          if (!token || !newPcoForCode) return;
          // Auto-map the unmapped code to the new PCO — that's the whole point
          // of opening this modal from an unmapped row. assignActivityCode
          // triggers a server-side rollup recompute internally.
          try {
            await api.settings.assignActivityCode(token, newPcoForCode.codeId, newPco.id);
          } catch (err) {
            toast.push({
              variant: 'warning',
              title: `Auto-map failed for ${newPcoForCode.code}`,
              body: `PCO was created, but the code couldn't be auto-mapped to it. Map it manually from the dropdown. (${
                err instanceof Error ? err.message : 'unknown error'
              })`,
            });
          }
          setNewPcoForCode(null);
          await onResolved();
        }}
      />
    </div>
  );
}

/* ===========================================================================
 * Project (single-row inline edit)
 * ======================================================================== */

function ProjectFields({
  project,
  onSaved,
}: {
  project: SettingsProject;
  onSaved: () => Promise<unknown>;
}) {
  const { token } = useAuth();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmSave, setConfirmSave] = useState(false);
  /**
   * The diff the confirm dialog is standing on. Derived once, when the dialog
   * opens, so the request sends exactly the edit the PM was shown.
   */
  const [pendingPatch, setPendingPatch] = useState<ProjectPatchRequest | null>(null);
  /**
   * Re-entrancy fence for `save()`. `busy` is state: two calls that land in the
   * same task both read the old `false` and both send.
   */
  const savingRef = useRef(false);
  const [name, setName] = useState(project.name);
  const [gcName, setGcName] = useState(project.gc_name);
  const [gcEmail, setGcEmail] = useState(project.gc_email);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>(
    project.gc_additional_emails,
  );
  /**
   * True for exactly one edit-mode entry: the read-only view's empty-state
   * "Add recipient" quick action. Tells AdditionalRecipientsEditor to
   * autofocus its (freshly-seeded) blank row the instant it mounts, so the
   * PM can start typing without an extra click into the field. Reset by
   * every other entry point (the "Edit project" button, and the row's own
   * click-to-edit on a non-empty roster) so it never fires on an ordinary
   * edit-mode entry and steals focus for no reason.
   */
  const [focusNewRecipient, setFocusNewRecipient] = useState(false);
  const [pmName, setPmName] = useState(project.pm_name);
  const [pmEmail, setPmEmail] = useState(project.pm_email);
  const [markupPct, setMarkupPct] = useState(String(project.markup_pct));
  const [equipmentTaxPct, setEquipmentTaxPct] = useState(String(project.equipment_tax_pct));
  const [perDiemDollars, setPerDiemDollars] = useState(
    (project.per_diem_rate_cents / 100).toFixed(2),
  );
  const [contractDollars, setContractDollars] = useState(
    project.original_contract_amount_cents != null
      ? (project.original_contract_amount_cents / 100).toFixed(2)
      : '',
  );
  const [defaultRateDollars, setDefaultRateDollars] = useState(
    (project.default_st_rate_cents / 100).toFixed(2),
  );
  const [defaultSwingRateDollars, setDefaultSwingRateDollars] = useState(
    (project.default_swing_rate_cents / 100).toFixed(2),
  );
  const [defaultPerDiem, setDefaultPerDiem] = useState(project.default_per_diem_eligible);
  const [calcOtDt, setCalcOtDt] = useState(project.calculate_ot_dt);
  const [sundayPremium, setSundayPremium] = useState(project.sunday_premium_enabled);
  const [sundayPremiumDollars, setSundayPremiumDollars] = useState(
    project.sunday_premium_rate_cents != null
      ? (project.sunday_premium_rate_cents / 100).toFixed(2)
      : '',
  );

  const reset = () => {
    setName(project.name);
    setGcName(project.gc_name);
    setGcEmail(project.gc_email);
    setAdditionalEmails(project.gc_additional_emails);
    setPmName(project.pm_name);
    setPmEmail(project.pm_email);
    setMarkupPct(String(project.markup_pct));
    setEquipmentTaxPct(String(project.equipment_tax_pct));
    setPerDiemDollars((project.per_diem_rate_cents / 100).toFixed(2));
    setContractDollars(
      project.original_contract_amount_cents != null
        ? (project.original_contract_amount_cents / 100).toFixed(2)
        : '',
    );
    setDefaultRateDollars((project.default_st_rate_cents / 100).toFixed(2));
    setDefaultSwingRateDollars((project.default_swing_rate_cents / 100).toFixed(2));
    setDefaultPerDiem(project.default_per_diem_eligible);
    setCalcOtDt(project.calculate_ot_dt);
    setSundayPremium(project.sunday_premium_enabled);
    setSundayPremiumDollars(
      project.sunday_premium_rate_cents != null
        ? (project.sunday_premium_rate_cents / 100).toFixed(2)
        : '',
    );
    setErr(null);
  };

  // Validate the form locally; returns a user-facing error string or null.
  const validate = (): string | null => {
    const markup = Number(markupPct);
    const equipTax = Number(equipmentTaxPct);
    const perDiem = Number(perDiemDollars);
    const contract = contractDollars.trim() === '' ? null : Number(contractDollars);
    const defaultRate = Number(defaultRateDollars);
    const defaultSwingRate = Number(defaultSwingRateDollars || '0');
    if (!Number.isFinite(markup) || markup < 0 || markup > 100) return 'markup 0-100';
    if (!Number.isFinite(equipTax) || equipTax < 0 || equipTax > 100)
      return 'equipment tax 0-100';
    if (!Number.isFinite(perDiem) || perDiem < 0) return 'per diem ≥ 0';
    if (contract !== null && !Number.isFinite(contract)) return 'contract must be a number';
    if (!Number.isFinite(defaultRate) || defaultRate < 0) return 'default rate ≥ 0';
    if (!Number.isFinite(defaultSwingRate) || defaultSwingRate < 0) return 'swing rate ≥ 0';
    // A blank rate is allowed while the premium is OFF (the pair is inert), but
    // switching it on without a rate would silently bill Sundays as normal days.
    const sundayRate = sundayPremiumDollars.trim() === '' ? null : Number(sundayPremiumDollars);
    if (sundayRate !== null && (!Number.isFinite(sundayRate) || sundayRate < 0))
      return 'Sunday premium rate ≥ 0';
    if (sundayPremium && (sundayRate === null || sundayRate <= 0))
      return 'Sunday premium needs a rate above $0';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(gcEmail.trim()))
      return 'GC recipient email must be a valid email';
    const trimmedAdditional = additionalEmails.map((e) => e.trim()).filter((e) => e !== '');
    for (const e of trimmedAdditional) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return `"${e}" is not a valid email`;
    }
    // MAX_GC_ADDITIONAL_EMAILS caps the count *after* de-dupe server-side;
    // check the raw count too so the error surfaces before save rather than
    // as a 400 the user doesn't understand.
    if (trimmedAdditional.length > MAX_GC_ADDITIONAL_EMAILS)
      return `Up to ${MAX_GC_ADDITIONAL_EMAILS} additional recipients`;
    if (!pmName.trim()) return 'PM name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pmEmail.trim()))
      return 'PM email must be a valid email';
    return null;
  };

  /** The whole form as a request body, before the diff narrows it. */
  const formPatch = (): ProjectPatchRequest => {
    const contract = contractDollars.trim() === '' ? null : Number(contractDollars);
    return {
      name: name.trim(),
      gc_name: gcName.trim(),
      gc_email: gcEmail.trim(),
      // Full replacement of the roster; the server does its own trim/
      // dedupe/blank-drop/cap, so a raw client-trimmed array is fine here.
      gc_additional_emails: additionalEmails.map((e) => e.trim()).filter((e) => e !== ''),
      pm_name: pmName.trim(),
      pm_email: pmEmail.trim(),
      markup_pct: Number(markupPct),
      equipment_tax_pct: Number(equipmentTaxPct),
      per_diem_rate_cents: Math.round(Number(perDiemDollars) * 100),
      original_contract_amount_cents: contract === null ? null : Math.round(contract * 100),
      default_st_rate_cents: Math.round(Number(defaultRateDollars) * 100),
      default_swing_rate_cents: Math.round(Number(defaultSwingRateDollars || '0') * 100),
      default_per_diem_eligible: defaultPerDiem,
      calculate_ot_dt: calcOtDt,
      sunday_premium_enabled: sundayPremium,
      // Blank clears the rate. The toggle and the rate are stored
      // independently, so switching the premium off keeps the rate on file.
      sunday_premium_rate_cents:
        sundayPremiumDollars.trim() === '' ? null : Math.round(Number(sundayPremiumDollars) * 100),
    };
  };

  // Validate up front so errors surface before the confirmation, not after.
  const requestSave = () => {
    const v = validate();
    if (v) return setErr(v);
    setErr(null);
    const patch = diffProjectPatch(project, formPatch());
    // Nothing moved: there is no blast radius to confirm and no write to make.
    // Confirming one would stamp updated_at and re-assert every rate on the job
    // for an edit the PM did not make.
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setPendingPatch(patch);
    setConfirmSave(true);
  };

  const save = async () => {
    if (savingRef.current) return;
    if (!token || !pendingPatch) return;
    const patch = pendingPatch;

    savingRef.current = true;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.settings.patchProject(token, patch);
      setConfirmSave(false);
      setPendingPatch(null);
      setEditing(false);
      // The edit landed but its follow-through did not. Ops already knows; the
      // PM needs to know too, because the numbers on screen may still be the
      // old ones and nothing else would say so. The sentence is deliberately
      // WIDE: the response reports only that something downstream failed, and
      // it may have been the roster fan-out or the stale-flagging rather than
      // the re-total — naming the totals alone would point the PM at the wrong
      // thing on two of the three failures.
      if (res.consequences_failed) {
        toast.push({
          variant: 'warning',
          body: 'Saved, but the follow-up work on this job’s totals and roster did not finish — support has been notified.',
        });
      }
      // Unconditional: the row DID take the edit, so the read card has to show
      // what is now stored either way.
      await onSaved();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Save failed';
      setErr(message);
      // The inline error renders in the edit footer, which the open dialog
      // covers — so on its own a 500 or a timeout left the PM watching a
      // dialog that appeared to do nothing. An `http` failure's message is a
      // machine code, which the footer can afford next to its fields and a
      // bare toast cannot; timeout/network already carry readable prose.
      const toastBody =
        e instanceof ApiCallError && e.kind !== 'http'
          ? e.message
          : "Couldn't save — try again, and contact support if it keeps happening.";
      toast.push({ variant: 'error', body: toastBody });
    } finally {
      savingRef.current = false;
      setBusy(false);
    }
  };

  /** Normal "Edit project" entry — no autofocus surprises. */
  const openEdit = () => {
    setFocusNewRecipient(false);
    setEditing(true);
  };

  /** "+ Add email" quick action from the read-only view: append one blank
   *  line (unless a blank already exists) and flag it for autofocus once
   *  AdditionalRecipientsEditor mounts. The blank is appended even when the
   *  list already has entries — without it, "Add" landed the PM focused on an
   *  EXISTING address and read as a mis-wired Edit (client, 2026-08-02). */
  const openEditWithNewRecipient = () => {
    setAdditionalEmails((prev) =>
      prev.some((e) => e.trim() === '') ? prev : [...prev, ''],
    );
    setFocusNewRecipient(true);
    setEditing(true);
  };

  if (!editing) {
    return (
      <>
        <div className="settings-grid">
          <div>
            {/* No mono anywhere on this card (Brandon 2026-08-05) — the mixed
                voices read strange; regular sans throughout. */}
            <FieldRowReadOnly label="Project name" value={project.name} />
            <FieldRowReadOnly label="Project number" value={project.project_number} />
            <FieldRowReadOnly label="General contractor" value={project.gc_name} />
            <FieldRowReadOnly label="GC recipient email" value={project.gc_email} />
            {/* Visible affordances in both states (client, 2026-08-02): the
                populated row used to be a secret click target — the emails
                looked like plain text and "+ Add" only existed while the list
                was empty. Now the list is text, the pencil edits, and
                "+ Add email" is always on the row. `mono` so the value cell
                matches the GC email row's weight (the serif fallback ran
                heavier — neither address should read bold). */}
            <FieldRowReadOnly
              label="Additional recipients"
              value={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                  }}
                >
                  {project.gc_additional_emails.length > 0 && (
                    <span>{project.gc_additional_emails.join(', ')}</span>
                  )}
                  {project.gc_additional_emails.length > 0 && (
                    <button
                      type="button"
                      className="icon-btn muted"
                      onClick={openEdit}
                      title="Edit additional recipients"
                      aria-label="Edit additional recipients"
                    >
                      <Pencil size={13} aria-hidden />
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-quiet"
                    onClick={openEditWithNewRecipient}
                    style={{ fontSize: 12 }}
                  >
                    + Add email
                  </button>
                </span>
              }
            />
            <FieldRowReadOnly label="Project manager" value={project.pm_name || '—'} />
            <FieldRowReadOnly label="PM email (Reply-To + CC)" value={project.pm_email || '—'} />
          </div>
          <div>
            <FieldRowReadOnly
              label="Original contract"
              value={
                project.original_contract_amount_cents
                  ? formatMoneyCents(project.original_contract_amount_cents)
                  : '—'
              }
            />
            <FieldRowReadOnly label="Project Markup" value={`${project.markup_pct}%`} />
            <FieldRowReadOnly label="Equipment Tax" value={`${project.equipment_tax_pct}%`} />
            <FieldRowReadOnly
              label="Default employee rate"
              value={formatMoneyCents(project.default_st_rate_cents, { precise: true })}
            />
            <FieldRowReadOnly
              label="Default swing rate"
              value={formatMoneyCents(project.default_swing_rate_cents, { precise: true })}
            />
            {/* Bare dollar figures (Brandon 2026-08-05) — per-hour is implied
                on both, and "/ total_hour" was the storage enum leaking. */}
            <FieldRowReadOnly
              label="Sunday premium"
              value={
                project.sunday_premium_enabled && project.sunday_premium_rate_cents
                  ? formatMoneyCents(project.sunday_premium_rate_cents, { precise: true })
                  : 'Off'
              }
            />
            <FieldRowReadOnly
              label="Per-diem rate"
              value={formatMoneyCents(project.per_diem_rate_cents, { precise: true })}
            />
            <FieldRowReadOnly
              label="Default per-diem eligible"
              value={project.default_per_diem_eligible ? 'Yes' : 'No'}
            />
            <FieldRowReadOnly
              label="Calculate OT & DT"
              value={project.calculate_ot_dt ? 'Yes' : 'No'}
            />
          </div>
        </div>
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <button
            type="button"
            className="btn-link btn-link-strong"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5 }}
            onClick={openEdit}
          >
            <SquarePen size={15} aria-hidden />
            Edit project
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="settings-grid">
        <div>
          <FieldRowInput label="Project name" value={name} onChange={setName} />
          <FieldRowReadOnly label="Project number" value={project.project_number} mono />
          <FieldRowInput label="General contractor" value={gcName} onChange={setGcName} />
          <FieldRowInput label="GC recipient email" value={gcEmail} onChange={setGcEmail} mono />
          <div className="field-row" style={{ alignItems: 'flex-start' }}>
            <div className="fr-label" style={{ paddingTop: 4 }}>
              Additional recipients
            </div>
            <div className="fr-value" style={{ textAlign: 'left' }}>
              <AdditionalRecipientsEditor
                emails={additionalEmails}
                onChange={setAdditionalEmails}
                autoFocusEmpty={focusNewRecipient}
              />
            </div>
          </div>
          <FieldRowInput label="Project manager" value={pmName} onChange={setPmName} />
          <FieldRowInput label="PM email (Reply-To + CC)" value={pmEmail} onChange={setPmEmail} mono />
          <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 4 }}>
            Approve &amp; Send shows the PM as the sender and routes {project.gc_name}&apos;s reply +
            a CC to this email. Changes apply to future sends only — already-submitted
            rollups keep the PM used at send time.
          </p>
        </div>
        <div>
          <FieldRowInput
            label="Original contract"
            value={contractDollars}
            onChange={setContractDollars}
            money
          />
          <FieldRowInput
            label="Project Markup (%)"
            value={markupPct}
            onChange={setMarkupPct}
            mono
          />
          <FieldRowInput
            label="Equipment Tax (%)"
            value={equipmentTaxPct}
            onChange={setEquipmentTaxPct}
            mono
          />
          <FieldRowInput
            label="Default employee rate"
            value={defaultRateDollars}
            onChange={setDefaultRateDollars}
            money
          />
          <FieldRowInput
            label="Default swing rate"
            value={defaultSwingRateDollars}
            onChange={setDefaultSwingRateDollars}
            money
          />
          <div className="field-row">
            <div className="fr-label">Sunday premium</div>
            <div className="fr-value">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Toggle
                  value={sundayPremium}
                  onChange={setSundayPremium}
                  aria-label="Sunday premium"
                />
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                  {sundayPremium ? 'On' : 'Off'}
                </span>
              </span>
            </div>
          </div>
          <FieldRowInput
            label="Sunday premium rate"
            value={sundayPremiumDollars}
            onChange={setSundayPremiumDollars}
            money
          />
          <FieldRowInput
            label="Per-diem rate"
            value={perDiemDollars}
            onChange={setPerDiemDollars}
            money
          />
          <div className="field-row">
            <div className="fr-label">Default per-diem eligible</div>
            <div className="fr-value">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Toggle
                  value={defaultPerDiem}
                  onChange={setDefaultPerDiem}
                  aria-label="Default per-diem eligible"
                />
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                  {defaultPerDiem ? 'Yes' : 'No'}
                </span>
              </span>
            </div>
          </div>
          <div className="field-row">
            <div className="fr-label">Calculate OT &amp; DT</div>
            <div className="fr-value">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Toggle value={calcOtDt} onChange={setCalcOtDt} aria-label="Calculate OT and DT" />
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                  {calcOtDt ? 'Yes' : 'No'}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        {err && <Err>{err}</Err>}
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            reset();
            setEditing(false);
          }}
          disabled={busy}
          style={{ marginRight: 8 }}
        >
          Cancel
        </button>
        <button type="button" className="btn btn-cta" onClick={requestSave} disabled={busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <ConfirmModal
        open={confirmSave}
        title="Save project changes?"
        body={
          <>
            These settings affect work that hasn&rsquo;t been billed yet:
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
              <li>
                Markup, per-diem, and rate changes recalculate every{' '}
                <strong>not-yet-submitted</strong> weekly rollup.
              </li>
              <li>
                Email changes (GC recipient, additional recipients, PM) apply to{' '}
                <strong>future</strong> Approve &amp; Send emails only.
              </li>
              <li>
                Rollups already submitted to {project.gc_name} stay frozen — they keep the numbers that were
                sent.
              </li>
            </ul>
          </>
        }
        confirmLabel="Save changes"
        cancelLabel="Cancel"
        busy={busy}
        busyLabel="Saving…"
        onConfirm={save}
        onCancel={() => {
          setConfirmSave(false);
          setPendingPatch(null);
        }}
      />
    </>
  );
}

function FieldRowReadOnly({
  label,
  value,
  mono,
  onClick,
  clickLabel,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  /** When provided, the value cell itself becomes a click-to-edit target —
   *  dotted-underline affordance (`.fr-value.editable`), no visual change
   *  otherwise. Used for values that are still just data (e.g. an address
   *  list) but shortcut straight into edit mode. */
  onClick?: () => void;
  /** Accessible name for the clickable cell; defaults to `label`. */
  clickLabel?: string;
}) {
  if (!onClick) {
    return (
      <div className="field-row">
        <div className="fr-label">{label}</div>
        <div className={`fr-value ${mono ? '' : 'serif'}`}>{value}</div>
      </div>
    );
  }
  return (
    <div className="field-row">
      <div className="fr-label">{label}</div>
      <div
        className={`fr-value editable ${mono ? '' : 'serif'}`}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        aria-label={clickLabel ?? label}
      >
        {value}
      </div>
    </div>
  );
}

function FieldRowInput({
  label,
  value,
  onChange,
  mono,
  money,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  money?: boolean;
}) {
  const sharedStyle: React.CSSProperties = {
    width: '100%',
    fontFamily: mono || money ? 'var(--mono)' : 'var(--sans)',
    fontSize: 13,
    textAlign: 'right',
    padding: '4px 0',
    border: 'none',
    borderBottom: '1px solid var(--rule-strong)',
    background: 'transparent',
    color: 'var(--ink)',
  };
  return (
    <div className="field-row">
      <div className="fr-label">{label}</div>
      <div className="fr-value">
        {money ? (
          <MoneyInput value={value} onChange={onChange} style={sharedStyle} />
        ) : (
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={sharedStyle}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Editable roster of additional Approve & Send recipients — up to
 * `MAX_GC_ADDITIONAL_EMAILS` free-text email lines with per-line remove and
 * an "+ Add recipient" affordance. Purely controlled (emails/onChange); the
 * parent (`ProjectFields`) owns the trim/validate/save cycle, same division
 * of labor as every other field on this form. Real validation happens in
 * `validate()` and again server-side — this component just keeps the array
 * in sync with each keystroke and blocks growing past the cap.
 *
 * Autofocus: whenever a row is appended (via `autoFocusEmpty` seeding the
 * initial length ref at 0, or via the "+ Add recipient" click while already
 * mounted), the newly-added blank input gets focus automatically so the PM
 * can start typing right away — no extra click into the field.
 */
function AdditionalRecipientsEditor({
  emails,
  onChange,
  autoFocusEmpty = false,
}: {
  emails: string[];
  onChange: (emails: string[]) => void;
  /** When true, this component just mounted with a freshly-seeded blank
   *  row (the read-only view's empty-state "+ Add recipient" quick action)
   *  and should focus it immediately. False for an ordinary edit-mode
   *  entry, where autofocusing an existing row would just steal focus. */
  autoFocusEmpty?: boolean;
}) {
  const { me } = useAuth();
  const atMax = emails.length >= MAX_GC_ADDITIONAL_EMAILS;
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  // Seeded once at mount: 0 when the caller says "focus the row that's
  // already here", so the first effect run sees a length increase and
  // focuses it; otherwise the current length, so an ordinary mount (editing
  // a roster that already has addresses) never autofocuses anything.
  const prevLength = useRef(autoFocusEmpty ? 0 : emails.length);

  useEffect(() => {
    if (emails.length > prevLength.current) {
      inputRefs.current[emails.length - 1]?.focus();
    }
    prevLength.current = emails.length;
  }, [emails.length]);

  const rowStyle: React.CSSProperties = {
    width: '100%',
    fontFamily: 'var(--mono)',
    fontSize: 13,
    textAlign: 'left',
    padding: '4px 0',
    border: 'none',
    borderBottom: '1px solid var(--rule-strong)',
    background: 'transparent',
    color: 'var(--ink)',
  };
  return (
    <div>
      {emails.map((email, i) => (
        // Index-as-key is fine here: rows are never reordered, only
        // appended/removed from a flat list, and the input is uncontrolled-
        // by-identity (its value always matches emails[i]).
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <input
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="email"
            value={email}
            placeholder="name@company.com"
            onChange={(e) => {
              const next = [...emails];
              next[i] = e.target.value;
              onChange(next);
            }}
            style={rowStyle}
          />
          <RemoveButton
            onClick={() => onChange(emails.filter((_, idx) => idx !== i))}
            label={`Remove ${email.trim() || 'recipient'}`}
          />
        </div>
      ))}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <button
          type="button"
          className="btn-quiet"
          onClick={() => onChange([...emails, ''])}
          disabled={atMax}
          title={atMax ? `Up to ${MAX_GC_ADDITIONAL_EMAILS} additional recipients` : undefined}
        >
          + Add email
        </button>
        <HelpTip title="Who else gets the send" learnMore="settings">
          Everyone listed here receives Approve &amp; Send emails alongside the primary GC
          recipient — use it to keep up as {gcName(me)}&apos;s cost team changes.
        </HelpTip>
      </div>
    </div>
  );
}

/* ===========================================================================
 * Master rate row — bulk-apply project defaults to every employee
 * ======================================================================== */

function MasterRateRow({
  project,
  employeeCount,
  onApplied,
  onSavedDefaults,
}: {
  project: SettingsProject;
  employeeCount: number;
  onApplied: () => Promise<unknown>;
  onSavedDefaults: () => Promise<unknown>;
}) {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  /** Which field (if any) is in inline edit mode. Two fields, one edit at a
   *  time — keeps the row layout stable. */
  const [editingField, setEditingField] = useState<null | 'rate' | 'per_diem'>(
    null,
  );
  /** Which field's bulk-apply is pending confirmation in the modal. Null when
   *  no confirm is showing. */
  const [applyConfirm, setApplyConfirm] = useState<null | 'rate' | 'per_diem'>(
    null,
  );
  const [rateDollars, setRateDollars] = useState(
    (project.default_st_rate_cents / 100).toFixed(2),
  );
  const [perDiem, setPerDiem] = useState(project.default_per_diem_eligible);

  const startEditRate = () => {
    setRateDollars((project.default_st_rate_cents / 100).toFixed(2));
    setMsg(null);
    setEditingField('rate');
  };

  const startEditPerDiem = () => {
    setPerDiem(project.default_per_diem_eligible);
    setMsg(null);
    setEditingField('per_diem');
  };

  const cancelEdit = () => {
    setRateDollars((project.default_st_rate_cents / 100).toFixed(2));
    setPerDiem(project.default_per_diem_eligible);
    setMsg(null);
    setEditingField(null);
  };

  const saveRate = async () => {
    if (!token) return;
    const rate = Number(rateDollars);
    if (!Number.isFinite(rate) || rate < 0) {
      setMsg('Rate must be a non-negative number.');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.settings.patchProject(token, {
        default_st_rate_cents: Math.round(rate * 100),
      });
      await onSavedDefaults();
      setEditingField(null);
      setMsg('Default rate saved.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const savePerDiem = async () => {
    if (!token) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.settings.patchProject(token, {
        default_per_diem_eligible: perDiem,
      });
      await onSavedDefaults();
      setEditingField(null);
      setMsg('Default per-diem saved.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const applyField = (field: 'rate' | 'per_diem') => {
    if (!token || employeeCount === 0) return;
    setApplyConfirm(field);
  };

  const applyConfirmLabel =
    applyConfirm === 'rate'
      ? `rate ${formatMoneyCents(project.default_st_rate_cents, { precise: true })}`
      : applyConfirm === 'per_diem'
        ? `per-diem ${project.default_per_diem_eligible ? 'on' : 'off'}`
        : '';

  const doApply = async () => {
    if (!token || applyConfirm === null) return;
    const opts =
      applyConfirm === 'rate'
        ? { apply_st_rate: true, apply_per_diem_eligible: false }
        : { apply_st_rate: false, apply_per_diem_eligible: true };
    setBusy(true);
    setMsg(null);
    try {
      const result = await api.settings.applyEmployeeDefaults(token, opts);
      setMsg(`Applied to ${result.updated_count} employees.`);
      await onApplied();
      setApplyConfirm(null);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Apply failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--rule)',
        borderRadius: 6,
        padding: '4px 16px',
        marginBottom: 12,
      }}
    >
      {/* Row 1: Default rate */}
      <MasterField
        label="Default rate"
        editing={editingField === 'rate'}
        valueView={
          <span className="mono" style={{ fontSize: 14, color: 'var(--ink)' }}>
            {formatMoneyCents(project.default_st_rate_cents, { precise: true })}
          </span>
        }
        editControl={
          <MoneyInput
            value={rateDollars}
            onChange={setRateDollars}
            disabled={busy}
            autoFocus
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid var(--rule-strong)',
              padding: '4px 0',
              fontFamily: 'var(--mono)',
              fontSize: 14,
              color: 'var(--ink)',
              width: 110,
              outline: 'none',
            }}
          />
        }
        onStartEdit={startEditRate}
        onSave={saveRate}
        onCancel={cancelEdit}
        onApply={() => applyField('rate')}
        applyDisabled={busy || employeeCount === 0}
        busy={busy}
      />
      <div style={{ height: 1, background: 'var(--rule)' }} />
      {/* Row 2: Default per diem */}
      <MasterField
        label="Default per diem"
        editing={editingField === 'per_diem'}
        valueView={
          <span style={{ fontSize: 14, color: 'var(--ink)' }}>
            {project.default_per_diem_eligible ? 'Eligible' : 'Not eligible'}
          </span>
        }
        editControl={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Toggle
              value={perDiem}
              onChange={setPerDiem}
              disabled={busy}
              aria-label="Default per diem eligible"
            />
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              {perDiem ? 'Eligible' : 'Not eligible'}
            </span>
          </span>
        }
        onStartEdit={startEditPerDiem}
        onSave={savePerDiem}
        onCancel={cancelEdit}
        onApply={() => applyField('per_diem')}
        applyDisabled={busy || employeeCount === 0}
        busy={busy}
      />
      {msg && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--ink-3)',
            paddingBottom: 8,
            paddingTop: 2,
          }}
        >
          {msg}
        </div>
      )}
      <ConfirmModal
        open={applyConfirm !== null}
        title={`Apply ${
          applyConfirm === 'rate' ? 'rate' : 'per-diem'
        } to all employees?`}
        body={`Set ${applyConfirmLabel} on all ${employeeCount} employees. Per-employee overrides will be replaced.`}
        confirmLabel="Apply"
        busyLabel="Applying…"
        busy={busy}
        onConfirm={() => void doApply()}
        onCancel={() => setApplyConfirm(null)}
      />
    </div>
  );
}

/**
 * One row in the master-defaults card. Read-mode shows the value + edit
 * icon + Apply button. Edit-mode swaps the value for an inline input and
 * shows Save / Cancel. Apply button stays disabled in edit-mode so Joey
 * can't accidentally apply the unsaved draft to all employees.
 */
function MasterField({
  label,
  editing,
  valueView,
  editControl,
  onStartEdit,
  onSave,
  onCancel,
  onApply,
  applyDisabled,
  busy,
}: {
  label: string;
  editing: boolean;
  valueView: ReactNode;
  editControl: ReactNode;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onApply: () => void;
  applyDisabled?: boolean;
  busy?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '12px 0',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 160 }}>
        <span className="eyebrow">{label}</span>
      </div>
      <div style={{ flex: '1 1 auto', minWidth: 160 }}>
        {editing ? editControl : valueView}
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {editing ? (
          <>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: 12 }}
              onClick={onCancel}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-cta"
              style={{ fontSize: 12 }}
              onClick={onSave}
              disabled={busy}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        ) : (
          <>
            <EditButton onClick={onStartEdit} label={`Edit ${label.toLowerCase()}`} />
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: 12 }}
              onClick={onApply}
              disabled={applyDisabled}
            >
              Apply to all
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The roster tabs' search box — the Compare rates window's box, matched: same
 * size and padding, the shared ✕ (.search-clear) only while there is
 * something to clear, and Escape empties it. Escape is stopped only when it
 * did something, so with the box empty the key still reaches whatever else
 * listens for it.
 */
function RosterSearch({
  kind,
  query,
  onChange,
}: {
  kind: RosterSearchKind;
  query: string;
  onChange: (next: string) => void;
}) {
  // Clearing unmounts the ✕ that did it, so focus goes back to the box.
  const searchRef = useRef<HTMLInputElement>(null);
  const clearSearch = () => {
    onChange('');
    searchRef.current?.focus();
  };
  const isEmployees = kind === 'employees';
  return (
    <div style={rosterSearchWrapStyle}>
      <input
        ref={searchRef}
        type="text"
        style={rosterSearchStyle}
        value={query}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && query !== '') {
            e.stopPropagation();
            clearSearch();
          }
        }}
        placeholder={isEmployees ? 'Search roster…' : 'Search assets…'}
        aria-label={
          isEmployees
            ? 'Search people by name, ID or classification'
            : 'Search assets by ID or description'
        }
      />
      {query !== '' && (
        <button
          type="button"
          className="search-clear"
          onClick={clearSearch}
          aria-label="Clear search"
        >
          <X size={13} strokeWidth={2} aria-hidden />
        </button>
      )}
    </div>
  );
}

type EditingChange = (id: string, open: boolean) => void;

/**
 * The ids of the roster rows whose edit form is open — so the table can keep
 * rendering them while a search that excludes them is typed (withOpenEdits).
 * The row reports; the table only reads.
 */
function useOpenEdits(): [ReadonlySet<string>, EditingChange] {
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  const onEditingChange = useCallback<EditingChange>((id, open) => {
    setOpenIds((prev) => {
      if (prev.has(id) === open) return prev;
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  return [openIds, onEditingChange];
}

/** A row's side of useOpenEdits: open while editing, closed on save, cancel
 *  or unmount. */
function useReportEditing(id: string, editing: boolean, onEditingChange: EditingChange) {
  useEffect(() => {
    if (!editing) return;
    onEditingChange(id, true);
    return () => onEditingChange(id, false);
  }, [id, editing, onEditingChange]);
}

// The row over each roster table: search + Compare rates on the left, + Add
// on the right. Wraps rather than overflowing on a narrow window.
const rosterControlsStyle: React.CSSProperties = {
  marginBottom: 8,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};

const rosterControlsLeftStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};

// Relative, so the ✕ can sit inside the box's right edge.
const rosterSearchWrapStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
};

// The Compare rates window's box, value for value.
const rosterSearchStyle: React.CSSProperties = {
  // Right padding clears the ✕ — text running under it reads as a typo.
  padding: '6px 26px 6px 8px',
  fontSize: 13,
  border: '1px solid var(--rule-strong, var(--rule))',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'var(--ink)',
  width: 260,
};

const rosterCountsStyle: React.CSSProperties = { fontSize: 12.5, margin: '0 0 8px' };

// The right-hand group: Apply to saved blocks, then + Add at the far edge.
const rosterControlsRightStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
};

const applyRatesLineStyle: React.CSSProperties = { margin: '10px 0 0' };

const applyRatesWeekListStyle: React.CSSProperties = {
  margin: '6px 0 0',
  paddingLeft: 18,
  fontSize: 13,
};

/**
 * "Apply to saved blocks" — a one-time re-price of this job's saved-but-unsent
 * work at the rates in this list. A block's rates freeze when it is saved, so
 * a rate change here never reaches work already saved on its own; this is the
 * explicit way to make it, and the blocks freeze again at the applied rate.
 * The click only previews: the counts and the money come back first, the
 * dialog states them, and nothing is written until Apply.
 */
function ApplyRatesToSavedButton({ kind }: { kind: ApplyRatesToSavedKind }) {
  const { token } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<ApplyRatesToSavedResponse | null>(null);
  const nothingToDo = preview !== null && preview.rows === 0;

  const openPreview = async () => {
    if (!token) return;
    setLoading(true);
    try {
      setPreview(await api.settings.applyRatesToSaved(token, { kind, confirm: false }));
    } catch (e) {
      toast.push({
        variant: 'error',
        title: "Couldn't check the saved blocks",
        body: e instanceof Error ? e.message : 'Try again, and contact support if it keeps happening.',
      });
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!token) return;
    setApplying(true);
    try {
      const result = await api.settings.applyRatesToSaved(token, { kind, confirm: true });
      setPreview(null);
      toast.push(applyRatesResultToast(result));
    } catch (e) {
      toast.push({
        variant: 'error',
        title: "Couldn't apply the rates",
        body: e instanceof Error ? e.message : 'Nothing was changed. Try again, and contact support if it keeps happening.',
      });
    } finally {
      setApplying(false);
    }
  };

  const sentBefore = preview ? applyRatesSentBeforeLine(preview.sent_before_weeks) : null;
  const unmatched = preview ? applyRatesUnmatchedLine(preview.skipped_unmatched_rows) : null;

  return (
    <>
      <button
        type="button"
        className="btn btn-ghost btn-accent"
        onClick={() => void openPreview()}
        disabled={loading}
      >
        Apply to saved blocks
      </button>
      <ConfirmModal
        open={preview !== null}
        title="Apply current rates to saved blocks"
        body={
          preview === null ? null : nothingToDo ? (
            <>
              <p style={{ margin: 0 }}>{applyRatesNothingText(preview)}</p>
              {unmatched !== null && (
                <p className="muted" style={applyRatesLineStyle}>
                  {unmatched}
                </p>
              )}
            </>
          ) : (
            <>
              <p style={{ margin: 0 }}>
                Re-prices every saved block on this job's open weeks, using the rates in this list. Weeks that have been sent and not reopened are not touched. Any rate typed by hand on a block will be replaced.
              </p>
              <p style={applyRatesLineStyle}>{applyRatesCountsLine(preview)}</p>
              <p style={applyRatesLineStyle}>{applyRatesDeltaLine(kind, preview.delta_cents)}</p>
              {preview.week_deltas.length <= APPLY_RATES_WEEK_LIST_MAX && (
                <ul style={applyRatesWeekListStyle}>
                  {preview.week_deltas.map((w) => (
                    <li key={applyRatesWeekKey(w)}>{applyRatesWeekLine(w)}</li>
                  ))}
                </ul>
              )}
              {sentBefore !== null && <p style={applyRatesLineStyle}>{sentBefore}</p>}
              {unmatched !== null && (
                <p className="muted" style={applyRatesLineStyle}>
                  {unmatched}
                </p>
              )}
              <p className="muted" style={applyRatesLineStyle}>
                This is a one-time update: a later change to this list won't move these blocks again.
              </p>
            </>
          )
        }
        confirmLabel="Apply"
        cancelLabel={nothingToDo ? 'Close' : 'Cancel'}
        acknowledgeOnly={nothingToDo}
        busy={applying}
        busyLabel="Applying…"
        onConfirm={() => void apply()}
        onCancel={() => setPreview(null)}
      />
    </>
  );
}

function EmployeesTable({
  rows,
  onChanged,
  project,
  knownClassifications,
}: {
  rows: SettingsEmployee[];
  onChanged: () => Promise<unknown>;
  /** Used to prefill the + Add employee form with project defaults. */
  project?: SettingsProject;
  knownClassifications: ReadonlyArray<string>;
}) {
  const { token } = useAuth();
  const [adding, setAdding] = useState(false);
  // Mounted only while open — the cross-job read is lazy, and nobody pays for
  // it by visiting the tab.
  const [comparing, setComparing] = useState(false);
  // Narrows the persisted rows only. The + Add form sits above the table, so
  // a search never hides the row being added, and a row open for editing
  // stays on screen whatever is typed (withOpenEdits).
  const [query, setQuery] = useState('');
  const [openEdits, onEditingChange] = useOpenEdits();
  const matched = filterEmployees(rows, query);
  const visible = withOpenEdits(rows, matched, openEdits);
  // Counted off the search alone: the line reports what the search found.
  const counts = rosterCountsLine({
    kind: 'employees',
    total: rows.length,
    shown: matched.length,
    query,
  });

  return (
    <>
      {/* Compare sits LEFT as an outlined button and Add stays RIGHT as the
          accent text-action: two blue text-actions side by side read as one
          phrase ("Compare rates + Add employee"), and Compare opens a view
          rather than adding a row, so it takes the ghost rank. The search
          box leads the left group, the same box the Compare window opens
          with. Apply to saved blocks acts on this list's rates, so it sits
          RIGHT beside + Add, at the ghost rank too: + Add stays the one
          accent action on the row. */}
      <div style={rosterControlsStyle}>
        <div style={rosterControlsLeftStyle}>
          {/* Nothing to search on an empty roster (RULES.md 7.7). */}
          {rows.length > 0 && (
            <RosterSearch kind="employees" query={query} onChange={setQuery} />
          )}
          <button type="button" className="btn btn-ghost" onClick={() => setComparing(true)}>
            Compare rates
          </button>
        </div>
        <div style={rosterControlsRightStyle}>
          {rows.length > 0 && <ApplyRatesToSavedButton kind="employees" />}
          <button type="button" className="btn-quiet" onClick={() => setAdding(true)}>
            + Add employee
          </button>
        </div>
      </div>
      {comparing && (
        <RateComparisonModal kind="employees" onClose={() => setComparing(false)} />
      )}
      {adding && (
        <EmployeeForm
          initial={{
            heavyjob_employee_id: '',
            name: '',
            classification: '',
            st_rate_cents: project?.default_st_rate_cents ?? 0,
            swing_rate_cents: null,
            primary_daypart: 'day',
            per_diem_eligible: project?.default_per_diem_eligible ?? false,
            active: true,
          }}
          project={project}
          knownClassifications={knownClassifications}
          onCancel={() => setAdding(false)}
          onSubmit={async (body) => {
            if (!token) return;
            await api.settings.createEmployee(token, body);
            setAdding(false);
            // A search typed before adding could hide the new row.
            setQuery('');
            await onChanged();
          }}
        />
      )}
      {rows.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>
          No employees in the roster yet. New ones surface in <em>Needs attention</em> on
          their first PDF; use the inline <em>Add to roster</em> action there to populate
          this list.
        </p>
      ) : visible.length === 0 ? (
        <EmptyState flush icon={SearchX} text={noMatchText('employees', query)} />
      ) : (
        <>
          {counts !== null && (
            <p className="muted" style={rosterCountsStyle} role="status">
              {counts}
            </p>
          )}
          <table className="table">
            <thead>
              <tr>
                <th>HJ ID</th>
                <th>Name</th>
                <th>Classification</th>
                <th>Primary Shift</th>
                <th className="num">Rate</th>
                <th className="center">Per diem</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => (
                <EmployeeRow
                  key={e.id}
                  row={e}
                  project={project}
                  onChanged={onChanged}
                  onEditingChange={onEditingChange}
                  knownClassifications={knownClassifications}
                />
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function EmployeeRow({
  row,
  project,
  onChanged,
  onEditingChange,
  knownClassifications,
}: {
  row: SettingsEmployee;
  /** Source of the Swing rate default when the row being edited has a null
   *  swing_rate_cents (legacy pre-sync row) and the PM flips it to Swing. */
  project?: SettingsProject;
  onChanged: () => Promise<unknown>;
  /** Tells the table this row's edit form is open, so a search can't unmount it. */
  onEditingChange: EditingChange;
  knownClassifications: ReadonlyArray<string>;
}) {
  const { token } = useAuth();
  const [editing, setEditing] = useState(false);
  useReportEditing(row.id, editing, onEditingChange);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const doRemove = async () => {
    if (!token) return;
    setBusy(true);
    try {
      await api.settings.deleteEmployee(token, row.id);
      await onChanged();
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <tr>
        <td colSpan={7}>
          <EmployeeForm
            initial={{
              heavyjob_employee_id: row.heavyjob_employee_id,
              name: row.name,
              classification: row.classification,
              st_rate_cents: row.st_rate_cents,
              swing_rate_cents: row.swing_rate_cents,
              primary_daypart: row.primary_daypart,
              ot_multiplier: row.ot_multiplier,
              dt_multiplier: row.dt_multiplier,
              per_diem_eligible: row.per_diem_eligible,
              active: row.active,
            }}
            project={project}
            knownClassifications={knownClassifications}
            onCancel={() => setEditing(false)}
            onSubmit={async (body) => {
              if (!token) return;
              await api.settings.updateEmployee(token, row.id, body);
              setEditing(false);
              await onChanged();
            }}
          />
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="mono" style={{ color: 'var(--ink)', fontSize: 13 }}>
        {row.heavyjob_employee_id}
      </td>
      <td style={{ color: 'var(--ink)' }}>{row.name}</td>
      <td className="muted">{row.classification}</td>
      <td className="muted">{row.primary_daypart === 'swing' ? 'Swing' : 'Day'}</td>
      <td className="num">
        {formatMoneyCents(
          row.primary_daypart === 'swing' ? (row.swing_rate_cents ?? row.st_rate_cents) : row.st_rate_cents,
          { precise: true },
        )}
      </td>
      <td className="center"><ActiveCheck active={row.per_diem_eligible} /></td>
      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <EditButton
            onClick={() => setEditing(true)}
            disabled={busy}
            label={`Edit ${row.name}`}
          />
          <RemoveButton
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            label={`Remove ${row.name}`}
          />
        </span>
        <ConfirmModal
          open={confirmOpen}
          title={`Remove ${row.name}?`}
          body="Historical labor rows are kept but the FK link to this employee is cleared."
          destructive
          confirmLabel="Remove"
          busyLabel="Removing…"
          busy={busy}
          onConfirm={() => void doRemove()}
          onCancel={() => setConfirmOpen(false)}
        />
      </td>
    </tr>
  );
}

function EmployeeForm({
  initial,
  project,
  onCancel,
  onSubmit,
  knownClassifications,
}: {
  initial: EmployeeWriteRequest;
  /** Project row — source of the Day/Swing rate defaults used to hand-hold
   *  the Rate field when Primary Shift is flipped to a shift with no rate
   *  entered yet. Optional only because a couple of call sites render this
   *  form before the project has loaded; the prefill is simply skipped then. */
  project?: SettingsProject;
  onCancel: () => void;
  onSubmit: (body: EmployeeWriteRequest) => Promise<void>;
  /** Existing distinct classifications across the project's employees.
   *  Drives the dropdown options; "Custom…" stays as the last entry so Joey
   *  can introduce a new classification (which auto-appears on next render). */
  knownClassifications?: ReadonlyArray<string>;
}) {
  const [hjId, setHjId] = useState(initial.heavyjob_employee_id);
  const [name, setName] = useState(initial.name);
  const [cls, setCls] = useState(initial.classification);
  // When the form opens, decide whether to start in dropdown or custom mode:
  // - Empty or matches an existing classification -> dropdown
  // - Doesn't match -> custom (the prior value isn't in the known set yet)
  const initialIsCustom =
    initial.classification.trim() !== '' &&
    knownClassifications !== undefined &&
    !knownClassifications.includes(initial.classification);
  const [clsCustom, setClsCustom] = useState<boolean>(initialIsCustom);
  const [rateDollars, setRateDollars] = useState((initial.st_rate_cents / 100).toFixed(2));
  const [swingRateDollars, setSwingRateDollars] = useState(
    initial.swing_rate_cents != null ? (initial.swing_rate_cents / 100).toFixed(2) : '',
  );
  const [primaryDaypart, setPrimaryDaypart] = useState<Daypart>(initial.primary_daypart ?? 'day');

  /**
   * Rate hand-holding on shift flip (2026-07-23, Brandon's live-testing
   * screenshot). Day and Swing rates are two independent fields
   * (rateDollars / swingRateDollars) toggled by which one the Select shows —
   * flipping shift never overwrites the field for the OTHER shift, so a
   * typed custom rate is naturally safe across any number of flips. The
   * actual gap: `swing_rate_cents` starts null for a brand-new employee (both
   * "+ Add employee" and "Add to roster" pass no swing rate) and can be null
   * on a legacy pre-sync row in Edit, so swingRateDollars starts at '' — flip
   * to Swing and the Rate field just sits empty at the $0.00 placeholder
   * instead of showing the project's $X.XX swing standard.
   *
   * Fix: when the user flips TO Swing and the field is still pristine (never
   * typed into — the same '' it started with, whether that's a brand-new
   * employee or a null-swing-rate legacy row), prefill it from
   * `project.default_swing_rate_cents`. The moment they type a real value it
   * stops being '' and this never fires again for the rest of the session,
   * so a custom rate is never clobbered by a later flip. `rateDollars` (Day)
   * needs no equivalent: st_rate_cents is NOT NULL in the schema, so it's
   * always a real number both for a new employee (project default, prefilled
   * today) and an existing one (their actual rate) — never empty, nothing to
   * hand-hold.
   */
  const handlePrimaryDaypartChange = (next: Daypart) => {
    setPrimaryDaypart(next);
    if (next === 'swing' && swingRateDollars.trim() === '' && project?.default_swing_rate_cents != null) {
      setSwingRateDollars((project.default_swing_rate_cents / 100).toFixed(2));
    }
  };
  const [ot, setOt] = useState(String(initial.ot_multiplier ?? 1.5));
  const [dt, setDt] = useState(String(initial.dt_multiplier ?? 2.0));
  const [perDiem, setPerDiem] = useState(initial.per_diem_eligible ?? false);
  const [active, setActive] = useState(initial.active ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const useDropdown = knownClassifications !== undefined && !clsCustom;

  const submit = async () => {
    if (!hjId.trim() || !name.trim() || !cls.trim()) {
      setErr('HJ ID, name, and classification are required.');
      return;
    }
    const rate = Number(rateDollars);
    if (!Number.isFinite(rate) || rate < 0) {
      setErr('Rate must be a non-negative number.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({
        heavyjob_employee_id: hjId.trim(),
        name: name.trim(),
        classification: cls.trim(),
        st_rate_cents: Math.round(rate * 100),
        swing_rate_cents: swingRateDollars.trim() === '' ? null : Math.round(Number(swingRateDollars) * 100),
        primary_daypart: primaryDaypart,
        ot_multiplier: Number(ot) || 1.5,
        dt_multiplier: Number(dt) || 2.0,
        per_diem_eligible: perDiem,
        active,
      });
    } catch (e) {
      setErr(rosterSaveErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <InlineForm
      onSubmit={submit}
      onCancel={onCancel}
      busy={busy}
      err={err}
      grid="84px minmax(150px, 2fr) minmax(130px, 1.5fr) 112px 100px 64px 64px 82px 70px"
    >
      <Input label="HJ ID" value={hjId} onChange={setHjId} mono />
      <Input label="Name" value={name} onChange={setName} />
      {useDropdown ? (
        <Select
          label="Classification"
          value={cls}
          onChange={(v) => {
            if (v === '__custom__') {
              setClsCustom(true);
              setCls('');
            } else {
              // Empty string is the explicit placeholder — keep state in
              // sync so submit validation surfaces the real "pick one" UX
              // instead of looking like a populated field that won't save.
              setCls(v);
            }
          }}
          // Lead with an empty placeholder so the dropdown visibly shows
          // "Select classification" when no value is picked yet (browsers
          // otherwise auto-display the first option, which makes the empty
          // state look populated and silently fails on submit).
          options={['', ...(knownClassifications ?? []), '__custom__']}
          renderOption={(o) =>
            o === '' ? 'Select classification…' : o === '__custom__' ? 'Custom…' : o
          }
        />
      ) : (
        <Input
          label={knownClassifications ? 'Classification (custom)' : 'Classification'}
          value={cls}
          onChange={setCls}
        />
      )}
      <Select
        label="Primary Shift"
        value={primaryDaypart}
        onChange={(v) => handlePrimaryDaypartChange(v as Daypart)}
        options={['day', 'swing']}
        renderOption={(o) => (o === 'day' ? 'Day' : 'Swing')}
      />
      {primaryDaypart === 'swing' ? (
        <Input label="Rate" value={swingRateDollars} onChange={setSwingRateDollars} money />
      ) : (
        <Input label="Rate" value={rateDollars} onChange={setRateDollars} money />
      )}
      <Input label="OT ×" value={ot} onChange={setOt} mono />
      <Input label="DT ×" value={dt} onChange={setDt} mono />
      <Checkbox label="Per diem" checked={perDiem} onChange={setPerDiem} />
      <Checkbox label="Active" checked={active} onChange={setActive} />
    </InlineForm>
  );
}

/* ===========================================================================
 * Equipment
 * ======================================================================== */

function EquipmentTable({
  rows,
  onChanged,
}: {
  rows: SettingsEquipment[];
  onChanged: () => Promise<unknown>;
}) {
  const { token } = useAuth();
  const [adding, setAdding] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [query, setQuery] = useState('');
  const [openEdits, onEditingChange] = useOpenEdits();
  const matched = filterEquipment(rows, query);
  const visible = withOpenEdits(rows, matched, openEdits);
  const counts = rosterCountsLine({
    kind: 'equipment',
    total: rows.length,
    shown: matched.length,
    query,
  });

  return (
    <>
      {/* Same layout as the Employees tab, for the same reason. */}
      <div style={rosterControlsStyle}>
        <div style={rosterControlsLeftStyle}>
          {rows.length > 0 && (
            <RosterSearch kind="equipment" query={query} onChange={setQuery} />
          )}
          <button type="button" className="btn btn-ghost" onClick={() => setComparing(true)}>
            Compare rates
          </button>
        </div>
        <div style={rosterControlsRightStyle}>
          {rows.length > 0 && <ApplyRatesToSavedButton kind="equipment" />}
          <button type="button" className="btn-quiet" onClick={() => setAdding(true)}>
            + Add equipment
          </button>
        </div>
      </div>
      {comparing && (
        <RateComparisonModal kind="equipment" onClose={() => setComparing(false)} />
      )}
      {adding && (
        <EquipmentForm
          initial={{ asset_id: '', description: '', type: 'rental', hourly_rate_cents: 0, active: true }}
          onCancel={() => setAdding(false)}
          onSubmit={async (body) => {
            if (!token) return;
            await api.settings.createEquipment(token, body);
            setAdding(false);
            setQuery('');
            await onChanged();
          }}
        />
      )}
      {rows.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>
          No equipment in the roster yet. New assets surface in <em>Needs attention</em> on
          their first PDF; use the inline <em>Add equipment</em> action there to populate
          this list.
        </p>
      ) : visible.length === 0 ? (
        <EmptyState flush icon={SearchX} text={noMatchText('equipment', query)} />
      ) : (
        <>
          {counts !== null && (
            <p className="muted" style={rosterCountsStyle} role="status">
              {counts}
            </p>
          )}
          <table className="table">
            <thead>
              <tr>
                <th>Asset ID</th>
                <th>Description</th>
                <th className="num">Hourly rate</th>
                <th className="center">Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => (
                <EquipmentRow
                  key={e.id}
                  row={e}
                  onChanged={onChanged}
                  onEditingChange={onEditingChange}
                />
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function EquipmentRow({
  row,
  onChanged,
  onEditingChange,
}: {
  row: SettingsEquipment;
  onChanged: () => Promise<unknown>;
  /** Tells the table this row's edit form is open, so a search can't unmount it. */
  onEditingChange: EditingChange;
}) {
  const { token } = useAuth();
  const [editing, setEditing] = useState(false);
  useReportEditing(row.id, editing, onEditingChange);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const doRemove = async () => {
    if (!token) return;
    setBusy(true);
    try {
      await api.settings.deleteEquipment(token, row.id);
      await onChanged();
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <tr>
        <td colSpan={5}>
          <EquipmentForm
            initial={{
              asset_id: row.asset_id,
              description: row.description,
              type: row.type,
              hourly_rate_cents: row.hourly_rate_cents,
              active: row.active,
            }}
            onCancel={() => setEditing(false)}
            onSubmit={async (body) => {
              if (!token) return;
              await api.settings.updateEquipment(token, row.id, body);
              setEditing(false);
              await onChanged();
            }}
          />
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td className="mono" style={{ color: 'var(--ink)' }}>{row.asset_id}</td>
      <td className="muted">{row.description}</td>
      <td className="num">{formatMoneyCents(row.hourly_rate_cents, { precise: true })}</td>
      <td className="center"><ActiveCheck active={row.active} /></td>
      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <EditButton
            onClick={() => setEditing(true)}
            disabled={busy}
            label={`Edit ${row.asset_id}`}
          />
          <RemoveButton
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            label={`Remove ${row.asset_id}`}
          />
        </span>
        <ConfirmModal
          open={confirmOpen}
          title={`Remove ${row.asset_id}?`}
          body="Historical equipment rows are kept but the FK link to this asset is cleared."
          destructive
          confirmLabel="Remove"
          busyLabel="Removing…"
          busy={busy}
          onConfirm={() => void doRemove()}
          onCancel={() => setConfirmOpen(false)}
        />
      </td>
    </tr>
  );
}

function EquipmentForm({
  initial,
  onCancel,
  onSubmit,
}: {
  initial: EquipmentWriteRequest;
  onCancel: () => void;
  onSubmit: (body: EquipmentWriteRequest) => Promise<void>;
}) {
  const [asset, setAsset] = useState(initial.asset_id);
  const [desc, setDesc] = useState(initial.description);
  const [rateDollars, setRateDollars] = useState((initial.hourly_rate_cents / 100).toFixed(2));
  const [active, setActive] = useState(initial.active ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // `type` column kept in the schema for future flexibility (owned vs rental
  // accounting will matter if Coffman ever wants to separate billable rate
  // from internal cost), but Brandon confirmed Coffman bills everything
  // hourly today and the distinction isn't used in any math. Preserve the
  // existing row's type when editing; default to 'rental' on create so the
  // dataset stays consistent.
  const preservedType: 'owned' | 'rental' = initial.type ?? 'rental';

  const submit = async () => {
    if (!asset.trim() || !desc.trim()) return setErr('Asset ID and description required.');
    const rate = Number(rateDollars);
    if (!Number.isFinite(rate) || rate < 0) return setErr('Rate must be a non-negative number.');
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({
        asset_id: asset.trim(),
        description: desc.trim(),
        type: preservedType,
        hourly_rate_cents: Math.round(rate * 100),
        active,
      });
    } catch (e) {
      setErr(rosterSaveErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <InlineForm onSubmit={submit} onCancel={onCancel} busy={busy} err={err} grid="repeat(4, 1fr)">
      <Input label="Asset ID" value={asset} onChange={setAsset} mono />
      <Input label="Description" value={desc} onChange={setDesc} />
      <Input label="Hourly rate" value={rateDollars} onChange={setRateDollars} money />
      <Checkbox label="Active" checked={active} onChange={setActive} />
    </InlineForm>
  );
}

/* ===========================================================================
 * Materials
 * ======================================================================== */

function MaterialsTable({
  rows,
  onChanged,
}: {
  rows: SettingsMaterial[];
  onChanged: () => Promise<unknown>;
}) {
  const { token } = useAuth();
  const [adding, setAdding] = useState(false);

  return (
    <>
      <div style={{ marginBottom: 8, textAlign: 'right' }}>
        <button type="button" className="btn-quiet" onClick={() => setAdding(true)}>
          + Add material
        </button>
      </div>
      {adding && (
        <MaterialForm
          initial={{ name: '', unit: '', default_rate_cents: 0, active: true }}
          onCancel={() => setAdding(false)}
          onSubmit={async (body) => {
            if (!token) return;
            await api.settings.createMaterial(token, body);
            setAdding(false);
            await onChanged();
          }}
        />
      )}
      {rows.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>
          No materials in the master list yet. Materials appear here once Joey confirms
          them from the queue with a default rate set.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Unit</th>
              <th className="num">Default rate</th>
              <th className="center">Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <MaterialRow key={m.id} row={m} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function MaterialRow({
  row,
  onChanged,
}: {
  row: SettingsMaterial;
  onChanged: () => Promise<unknown>;
}) {
  const { token } = useAuth();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const doRemove = async () => {
    if (!token) return;
    setBusy(true);
    try {
      await api.settings.deleteMaterial(token, row.id);
      await onChanged();
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <tr>
        <td colSpan={5}>
          <MaterialForm
            initial={{
              name: row.name,
              unit: row.unit,
              default_rate_cents: row.default_rate_cents,
              active: row.active,
            }}
            onCancel={() => setEditing(false)}
            onSubmit={async (body) => {
              if (!token) return;
              await api.settings.updateMaterial(token, row.id, body);
              setEditing(false);
              await onChanged();
            }}
          />
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td className="name serif">{row.name}</td>
      <td className="muted">{row.unit}</td>
      <td className="num">{formatMoneyCents(row.default_rate_cents, { precise: true })}</td>
      <td className="center"><ActiveCheck active={row.active} /></td>
      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <EditButton
            onClick={() => setEditing(true)}
            disabled={busy}
            label={`Edit ${row.name}`}
          />
          <RemoveButton
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            label={`Remove ${row.name}`}
          />
        </span>
        <ConfirmModal
          open={confirmOpen}
          title={`Remove ${row.name}?`}
          body="Materials queue items already confirmed against it will lose their FK link."
          destructive
          confirmLabel="Remove"
          busyLabel="Removing…"
          busy={busy}
          onConfirm={() => void doRemove()}
          onCancel={() => setConfirmOpen(false)}
        />
      </td>
    </tr>
  );
}

function MaterialForm({
  initial,
  onCancel,
  onSubmit,
}: {
  initial: MaterialWriteRequest;
  onCancel: () => void;
  onSubmit: (body: MaterialWriteRequest) => Promise<void>;
}) {
  const [name, setName] = useState(initial.name);
  const [unit, setUnit] = useState(initial.unit);
  const [rateDollars, setRateDollars] = useState((initial.default_rate_cents / 100).toFixed(2));
  const [active, setActive] = useState(initial.active ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || !unit.trim()) return setErr('Name and unit required.');
    const rate = Number(rateDollars);
    if (!Number.isFinite(rate) || rate < 0) return setErr('Rate must be a non-negative number.');
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({
        name: name.trim(),
        unit: unit.trim(),
        default_rate_cents: Math.round(rate * 100),
        active,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <InlineForm onSubmit={submit} onCancel={onCancel} busy={busy} err={err} grid="repeat(4, 1fr)">
      <Input label="Name" value={name} onChange={setName} />
      <Input label="Unit" value={unit} onChange={setUnit} mono />
      <Input label="Default rate ($)" value={rateDollars} onChange={setRateDollars} mono />
      <Checkbox label="Active" checked={active} onChange={setActive} />
    </InlineForm>
  );
}

/* ===========================================================================
 * PCOs
 * ======================================================================== */

function PcosTable({
  rows,
  onChanged,
}: {
  rows: SettingsPco[];
  onChanged: () => Promise<unknown>;
}) {
  const { token, me } = useAuth();
  const [adding, setAdding] = useState(false);

  return (
    <>
      <div style={{ marginBottom: 8, textAlign: 'right' }}>
        <button type="button" className="btn-quiet" onClick={() => setAdding(true)}>
          + Add PCO
        </button>
      </div>
      {adding && (
        <PcoForm
          initial={{
            pco_number: '',
            description: '',
            status: 'open',
            // Same safe default as the column; the select makes it visible
            // and changeable instead of an invisible fallback.
            billing_type: 'lump_sum',
            notes: null,
            amount_proposed_cents: null,
            comments: null,
            is_no_cost: false,
          }}
          onCancel={() => setAdding(false)}
          onSubmit={async (body) => {
            if (!token) return;
            await api.settings.createPco(token, body);
            setAdding(false);
            await onChanged();
          }}
        />
      )}
      {rows.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>
          No parent PCOs yet. Use <em>+ New PCO</em> in the side rail to create one from
          {' '}{gcName(me)}'s contract paperwork; add MOD/FWA approvals via the PCO's funding
          buckets.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>PCO</th>
              <th>Description</th>
              <th className="num">Proposed</th>
              <th className="num">Approved</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <PcoRow key={p.id} row={p} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function PcoRow({ row, onChanged }: { row: SettingsPco; onChanged: () => Promise<unknown> }) {
  const { token, me } = useAuth();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeErr, setRemoveErr] = useState<string | null>(null);

  // What the list body already tells us about billing behind this PCO. The
  // server's 409 is the actual gate — this only spares the PM a click on a
  // PCO we can already see has history (any submission, or any rollup, which
  // is what a non-null latest week means).
  const hasBilling = row.submission_count > 0 || row.latest_week_ending_date != null;

  const doRemove = async () => {
    if (!token) return;
    setBusy(true);
    setRemoveErr(null);
    try {
      await api.settings.deletePco(token, row.id);
      await onChanged();
      setConfirmOpen(false);
    } catch (e) {
      const payload = e instanceof ApiCallError ? e.payload : null;
      setRemoveErr(
        (payload && 'message' in payload && typeof payload.message === 'string'
          ? payload.message
          : null) ?? (e instanceof Error ? e.message : 'Remove failed.'),
      );
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <tr>
        <td colSpan={6}>
          <PcoForm
            initial={{
              pco_number: row.pco_number,
              description: row.description,
              status: row.status,
              billing_type: row.billing_type,
              notes: row.notes,
              amount_proposed_cents: row.amount_proposed_cents,
              comments: row.comments,
              void_reason: row.void_reason,
              is_no_cost: row.is_no_cost,
            }}
            onCancel={() => setEditing(false)}
            onSubmit={async (body) => {
              if (!token) return;
              await api.settings.updatePco(token, row.id, body);
              setEditing(false);
              await onChanged();
            }}
          />
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td className="mono">{formatPcoNumber(row.pco_number)}</td>
      <td className="name serif">{row.description}</td>
      <td className="num">
        {row.amount_proposed_cents != null
          ? formatMoneyCents(row.amount_proposed_cents)
          : row.is_no_cost
            ? <span className="muted">No cost</span>
            : '—'}
      </td>
      <td className="num">
        {row.approved_total_cents > 0 ? formatMoneyCents(row.approved_total_cents) : '—'}
      </td>
      <td className="muted">{row.status}</td>
      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <EditButton
            onClick={() => setEditing(true)}
            disabled={busy}
            label={`Edit PCO ${formatPcoNumber(row.pco_number)}`}
          />
          <RemoveButton
            onClick={() => setConfirmOpen(true)}
            disabled={busy || hasBilling}
            label={
              hasBilling
                ? `PCO ${formatPcoNumber(row.pco_number)} has been billed — void it instead of deleting`
                : `Remove PCO ${formatPcoNumber(row.pco_number)}`
            }
          />
        </span>
        <ConfirmModal
          open={confirmOpen}
          title={`Remove PCO ${formatPcoNumber(row.pco_number)}?`}
          body={
            <>
              <div>
                Deleting is for a PCO created by mistake. It removes the PCO and unmaps any
                activity codes pointing at it.
              </div>
              <div>
                A PCO that has been billed can&apos;t be deleted — if it has any billing package,
                any weekly rollup, or any saved daily block, this will be refused. Set its status
                to <strong>Void</strong> instead: voiding asks for a reason, keeps every record of
                what was sent to {gcName(me)}, and can be undone.
              </div>
              {removeErr && <Err>{removeErr}</Err>}
            </>
          }
          destructive
          confirmLabel="Remove PCO"
          busyLabel="Removing…"
          busy={busy}
          onConfirm={() => void doRemove()}
          onCancel={() => {
            setRemoveErr(null);
            setConfirmOpen(false);
          }}
        />
      </td>
    </tr>
  );
}

function PcoForm({
  initial,
  onCancel,
  onSubmit,
}: {
  initial: PcoWriteRequest;
  onCancel: () => void;
  onSubmit: (body: PcoWriteRequest) => Promise<void>;
}) {
  const [num, setNum] = useState(initial.pco_number);
  const [desc, setDesc] = useState(initial.description);
  const [proposedDollars, setProposedDollars] = useState(
    initial.amount_proposed_cents != null ? (initial.amount_proposed_cents / 100).toFixed(2) : '',
  );
  const [status, setStatus] = useState<PcoWriteRequest['status']>(initial.status ?? 'open');
  // Explicit rather than implicit: this form also creates PCOs, and leaving
  // billing_type off the body would silently file every one as lump_sum.
  const [billingType, setBillingType] = useState<PcoBillingType>(
    initial.billing_type ?? 'lump_sum',
  );
  const [comments, setComments] = useState(initial.comments ?? '');
  const [voidReason, setVoidReason] = useState(initial.void_reason ?? '');
  const [isNoCost, setIsNoCost] = useState(initial.is_no_cost ?? false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!num.trim() || !desc.trim()) return setErr('PCO number and description required.');
    let proposed: number | null = null;
    if (proposedDollars.trim() !== '') {
      const n = Number(proposedDollars);
      // No sign check — a deduct/credit PCO's proposed figure is negative
      // (063, −$2M), and this form SEEDS itself from the stored value, so a
      // `< 0` refusal blocked saving a row nobody had even edited. The server
      // accepts negatives here for the same reason.
      if (!Number.isFinite(n)) return setErr('Proposed amount must be a number, or empty.');
      proposed = Math.round(n * 100);
    }
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({
        pco_number: num.trim(),
        description: desc.trim(),
        status,
        billing_type: billingType,
        amount_proposed_cents: proposed,
        comments: comments.trim() || null,
        void_reason: status === 'void' ? (voidReason.trim() || null) : null,
        is_no_cost: isNoCost,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <InlineForm onSubmit={submit} onCancel={onCancel} busy={busy} err={err} grid="repeat(6, 1fr)">
      <Input label="PCO #" value={num} onChange={setNum} mono />
      <Input label="Description" value={desc} onChange={setDesc} />
      <Input label="Amount proposed" value={proposedDollars} onChange={setProposedDollars} money />
      {/* Status collapsed to the three lifecycle values that are still set by
          hand — open/paid/void (funding buckets, finding #8). The active
          family (active / pending_approval / approved) is now derived from the
          money picture; a legacy row carrying one of those keeps it as an
          extra option so its stored value isn't silently rewritten. */}
      <Select
        label="Status"
        value={status ?? 'open'}
        onChange={(v) => setStatus(v as PcoWriteRequest['status'])}
        options={
          (['open', 'paid', 'void'] as string[]).includes(status ?? 'open')
            ? ['open', 'paid', 'void']
            : [status as string, 'open', 'paid', 'void']
        }
        renderOption={(o) => (o === 'open' ? 'Open / active' : o)}
      />
      <Select
        label="Billing method"
        value={billingType}
        onChange={(v) => setBillingType(v as PcoBillingType)}
        options={['weekly_rollup', 'lump_sum']}
        renderOption={(o) => (o === 'weekly_rollup' ? 'T&M Weekly Rollup' : 'Lump-Sum')}
      />
      <Input label="Comments" value={comments} onChange={setComments} />
      <Checkbox label="No cost" checked={isNoCost} onChange={setIsNoCost} />
      {status === 'void' && (
        <Input label="Void reason" value={voidReason} onChange={setVoidReason} />
      )}
    </InlineForm>
  );
}

/* ===========================================================================
 * Activity codes (inline PCO assignment)
 * ======================================================================== */

/**
 * Sentinel `<option>` value for "Ignore this code". Not a PCO id and not the
 * empty string (which means "Needs assignment"), so it can't collide with
 * either — the select's onChange branches on it before touching the mapping
 * endpoint.
 */
const IGNORE_OPTION = '__ignore__';

/**
 * The IGNORE list, as its own collapsed section under the PCOs tab.
 *
 * These are codes the PM has decided will never bill through the portal —
 * contract / lump-sum scope foremen still tag on their dailies. They are
 * deliberately NOT in "Activity codes without a PCO" (that count is a to-do
 * list) and NOT in "Activity code mappings" (they have no mapping and can't
 * take one), so this is the only place they appear — one row, one way back.
 */
function IgnoredCodesSection({
  rows,
  onChanged,
}: {
  rows: SettingsActivityCode[];
  onChanged: () => Promise<unknown>;
}) {
  const { token } = useAuth();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  const unignore = async (codeId: string, code: string) => {
    if (!token) return;
    setBusyId(codeId);
    try {
      const result = await api.settings.setActivityCodeIgnored(token, codeId, false);
      toast.push({
        variant: 'success',
        title: `${code} is no longer ignored`,
        body:
          result.blocks_changed > 0
            ? `${result.blocks_changed} block${result.blocks_changed === 1 ? '' : 's'} returned to the PDF Inbox for review.`
            : 'Future PDFs carrying this code will arrive in the PDF Inbox again.',
      });
      await onChanged();
    } catch (e) {
      toast.push({
        variant: 'warning',
        title: `Couldn't reactivate ${code}`,
        body: e instanceof Error ? e.message : 'Please try again.',
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SettingsSection
      title={`Ignored codes (${rows.length})`}
      tipTitle="What ignoring a code does"
      tip="Codes that never bill through the portal — contract or lump-sum work foremen still tag on their dailies. Blocks under these codes skip the PDF Inbox entirely. Reactivate one and its blocks come back for review."
      collapsible
      defaultExpanded={false}
    >
      <table className="table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Description from PDF</th>
            <th>Ignored</th>
            <th>By</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id}>
              <td className="mono">{c.code}</td>
              <td className="muted">{c.description_from_pdf ?? '—'}</td>
              <td className="muted">
                {c.ignored_at ? formatDateShort(c.ignored_at.slice(0, 10)) : '—'}
              </td>
              <td className="muted">{c.ignored_by_name ?? '—'}</td>
              <td className="num">
                <button
                  type="button"
                  className="btn-quiet"
                  disabled={busyId === c.id}
                  onClick={() => void unignore(c.id, c.code)}
                >
                  {busyId === c.id ? 'Reactivating…' : 'Reactivate'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SettingsSection>
  );
}

function ActivityCodesTable({
  rows,
  pcos,
  onChanged,
}: {
  rows: SettingsActivityCode[];
  pcos: SettingsPco[];
  onChanged: () => Promise<unknown>;
}) {
  if (rows.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 13 }}>
        No activity codes seen yet. Codes appear here automatically as foremen submit
        PDFs that reference them — then assign each one to a parent PCO inline.
      </p>
    );
  }
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Code</th>
          <th>Description from PDF</th>
          <th>Mapped PCO</th>
          <th>First seen</th>
          <th>Last seen</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <ActivityRemapRow
            key={c.id}
            codeId={c.id}
            code={c.code}
            description={c.description_from_pdf}
            firstSeen={c.first_seen_at}
            lastSeen={c.last_seen_at}
            currentPcoId={c.mapped_pco?.id ?? null}
            pcos={pcos}
            onChanged={onChanged}
          />
        ))}
      </tbody>
    </table>
  );
}

function ActivityRemapRow({
  codeId,
  code,
  description,
  firstSeen,
  lastSeen,
  currentPcoId,
  pcos,
  onChanged,
  latestDailyPdfId,
  onPreviewPdf,
  onCreateNewPco,
  onIgnore,
}: {
  codeId: string;
  code: string;
  description: string | null;
  firstSeen: string;
  lastSeen?: string;
  currentPcoId: string | null;
  pcos: SettingsPco[];
  onChanged: () => Promise<unknown>;
  /**
   * Optional "Ignore this code" option in the PCO dropdown. Only passed where
   * a code can legally be ignored — the unmapped-codes callout. A code already
   * mapped to a PCO can't be ignored (the server 409s `code_is_mapped`), so
   * the main mappings table doesn't offer it.
   */
  onIgnore?: () => Promise<unknown>;
  /** Most-recent PDF where this code appeared. Surfaces a View PDF link. */
  latestDailyPdfId?: string | null;
  onPreviewPdf?: (id: string) => void;
  /**
   * Optional "+ New PCO" action — opens a modal that creates a new PCO and
   * auto-maps this code to it. Only surfaced in UnknownsSection (where Joey
   * is resolving codes Hoffman just issued FWAs for) — not in the main
   * ActivityCodesTable where codes already have mappings.
   */
  onCreateNewPco?: () => void;
}) {
  const { token } = useAuth();
  // Display derives from the server-truth prop; local state only carries the
  // in-flight pick. The same code renders in both the unknowns callout and the
  // main mappings table, so a save in one must show in the other after refetch —
  // a useState copy of the prop goes stale there.
  const [pendingPick, setPendingPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Informational confirmation after a forward-only remap. No blocking modal —
  // saved/submitted work is never touched, so there's nothing to confirm.
  const [postRemapToast, setPostRemapToast] = useState<string | null>(null);

  const value = pendingPick ?? currentPcoId ?? '';

  const onPick = async (next: string) => {
    if (!token) return;
    if (next === value) return;
    // IGNORE is a third answer to "where does this code go?", so it lives in
    // the same control as the PCO choices rather than as a stray button — the
    // question and every answer to it stay in one place. Its own endpoint,
    // its own confirm: this is the one option that changes existing blocks.
    if (next === IGNORE_OPTION) {
      if (!onIgnore) return;
      await onIgnore();
      return;
    }
    setPendingPick(next);
    setBusy(true);
    setErr(null);
    setPostRemapToast(null);
    try {
      const result = await api.settings.assignActivityCode(
        token,
        codeId,
        next === '' ? null : next,
      );
      const n = result.pending_blocks_updated;
      setPostRemapToast(
        n > 0
          ? `Mapping updated. ${n} pending block${n === 1 ? '' : 's'} updated to the new PCO.`
          : 'Mapping updated.',
      );
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setPendingPick(null);
      setBusy(false);
    }
  };

  return (
    <tr>
      <td className="mono">{code}</td>
      <td className="muted">{description ?? '—'}</td>
      <td>
        <Dropdown
          uiSize="sm"
          value={value}
          onChange={(v) => void onPick(v)}
          disabled={busy}
          aria-label={`Assign ${code} to a PCO`}
          style={{ minWidth: 220 }}
          options={[
            { value: '', label: 'Needs assignment' },
            ...pcos.map((p) => ({
              value: p.id,
              label: `${formatPcoNumber(p.pco_number)} — ${p.description}`,
            })),
            ...(onIgnore
              ? [{ value: IGNORE_OPTION, label: 'Ignore this code — never bills here' }]
              : []),
          ]}
        />
        {busy && <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>Saving…</span>}
        {err && (
          <span style={{ marginLeft: 8, color: 'var(--danger)', fontSize: 12 }}>{err}</span>
        )}
        {postRemapToast && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 12,
              color: 'var(--positive)',
            }}
          >
            {postRemapToast}
          </span>
        )}
        {onCreateNewPco && (
          <button
            type="button"
            className="btn-quiet"
            onClick={onCreateNewPco}
            style={{ marginLeft: 12, fontSize: 12 }}
          >
            + New PCO
          </button>
        )}
      </td>
      <td className="muted">{formatDateShort(firstSeen.slice(0, 10))}</td>
      <td className="muted">{lastSeen ? formatDateShort(lastSeen.slice(0, 10)) : '—'}</td>
      {onPreviewPdf && (
        <td className="num">
          {latestDailyPdfId && (
            <button
              type="button"
              className="btn-link"
              onClick={() => onPreviewPdf(latestDailyPdfId)}
            >
              View PDF <ArrowRight size={13} aria-hidden />
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

/* ===========================================================================
 * Generic form primitives
 * ======================================================================== */

function InlineForm({
  children,
  onSubmit,
  onCancel,
  busy,
  err,
  grid,
}: {
  children: ReactNode;
  onSubmit: () => void;
  onCancel: () => void;
  busy: boolean;
  err: string | null;
  grid: string;
}) {
  return (
    <div
      style={{
        padding: '14px 16px',
        background: 'var(--surface-warm)',
        border: '1px solid var(--rule-strong)',
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: '8px 16px' }}>{children}</div>
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        {err && <Err>{err}</Err>}
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onCancel}
          disabled={busy}
          style={{ marginRight: 8, background: 'var(--surface)' }}
        >
          Cancel
        </button>
        <button type="button" className="btn btn-cta" onClick={onSubmit} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  mono,
  money,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  money?: boolean;
}) {
  const inputStyle: React.CSSProperties = {
    fontFamily: mono || money ? 'var(--mono)' : 'var(--sans)',
    fontSize: 13,
    padding: '4px 6px',
    border: '1px solid var(--rule)',
    background: 'var(--surface)',
    color: 'var(--ink)',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label className="field-label">{label}</label>
      {money ? (
        <MoneyInput value={value} onChange={onChange} style={inputStyle} />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      )}
    </div>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (b: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label className="field-label">{label}</label>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Toggle value={checked} onChange={onChange} aria-label={label} />
        <span className="muted" style={{ fontSize: 12 }}>{checked ? 'Yes' : 'No'}</span>
      </span>
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  renderOption,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<string>;
  /** Optional label transform — used by the classification picker so the
   *  sentinel value "__custom__" renders as "Custom…". */
  renderOption?: (option: string) => string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label className="field-label">{label}</label>
      <Dropdown
        uiSize="sm"
        value={value}
        onChange={onChange}
        aria-label={label}
        options={options.map((o) => ({ value: o, label: renderOption ? renderOption(o) : o }))}
      />
    </div>
  );
}

/* ===========================================================================
 * Users (admin only)
 *
 * WHO CAN SEE THIS: the tab button hides for a non-admin and the panel body
 * refuses independently, because `#users` is typeable. Both are courtesies —
 * `/api/users` is admin-gated server-side and is the actual control.
 *
 * WHAT THIS TAB DOES NOT DO: delete anyone. "Remove access" deactivates, which
 * is reversible and is the only safe option — a hard delete fails outright for
 * anyone who ever hit Approve & Send, silently destroys their feedback history,
 * and anonymises every audit row they appear on.
 *
 * Copy on destructive actions states the CONSEQUENCE rather than asking "are
 * you sure?". Joey should never have to guess what a button does to Josh.
 * ======================================================================== */

/** Plain-English "what does this role actually let someone do". */
const ROLE_BLURB: Record<UserRole, string> = {
  admin: 'Can do everything a project manager can, plus add and remove people.',
  pm: 'Can do all the billing work — inbox, rollups, Approve & Send. Cannot manage users.',
};

const ROLE_LABEL: Record<UserRole, string> = { admin: 'Administrator', pm: 'Project manager' };

/** "3d ago" / "just now". The absolute date rides in a title attribute. */
function relativeAge(iso: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDateShort(iso.slice(0, 10));
}

/** "4 min" for a live lock — the countdown the PM reads before clicking Unlock. */
function unlocksIn(iso: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const mins = Math.ceil(ms / 60_000);
  return mins <= 1 ? 'under a minute' : `${mins} min`;
}

/** Role options for the per-row picker, in the order the Add form offers them. */
const ROLE_OPTIONS = [
  { value: 'pm', label: ROLE_LABEL.pm },
  { value: 'admin', label: ROLE_LABEL.admin },
];

function UsersPanel() {
  const users = useAdminUsers();
  const [adding, setAdding] = useState(false);
  // Collapsed on every load, deliberately: people who no longer have access
  // are history, not the working list. No persistence — a fresh visit should
  // show the people who can actually sign in.
  const [showRemoved, setShowRemoved] = useState(false);

  const rows = users.data?.users ?? [];
  const active = rows.filter((u) => u.is_active);
  const removed = rows.filter((u) => !u.is_active);

  return (
    <SettingsSection title="Users">
      <div style={{ marginBottom: 8, textAlign: 'right' }}>
        <button type="button" className="btn-quiet" onClick={() => setAdding(true)}>
          + Add user
        </button>
      </div>

      {adding && (
        <AddUserForm
          onCancel={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await users.refetch();
          }}
        />
      )}

      {users.error && !users.data && (
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>
          Couldn&rsquo;t load the user list. {users.error.message}
        </p>
      )}

      {users.data ? (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  Role
                  <HelpTip title="What each role can do" learnMore="settings">
                    <strong>Project manager</strong> — {ROLE_BLURB.pm}
                    <br />
                    <strong>Administrator</strong> — {ROLE_BLURB.admin}
                  </HelpTip>
                </span>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {active.map((u) => (
              <UserRow key={u.id} user={u} onChanged={users.refetch} />
            ))}
            {removed.length > 0 && (
              <tr>
                <td colSpan={4} style={{ padding: '8px 14px' }}>
                  <button
                    type="button"
                    className="btn-link muted"
                    style={{ fontSize: 12 }}
                    aria-expanded={showRemoved}
                    onClick={() => setShowRemoved((v) => !v)}
                  >
                    {removed.length} removed account{removed.length === 1 ? '' : 's'} —{' '}
                    {showRemoved ? 'hide' : 'show'}
                  </button>
                </td>
              </tr>
            )}
            {showRemoved &&
              removed.map((u) => <UserRow key={u.id} user={u} onChanged={users.refetch} />)}
          </tbody>
        </table>
      ) : (
        !users.error && <Loading />
      )}
    </SettingsSection>
  );
}

function AddUserForm({
  onCancel,
  onAdded,
}: {
  onCancel: () => void;
  onAdded: () => Promise<unknown>;
}) {
  const { token } = useAuth();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('pm');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!token) return;
    if (!email.trim() || !name.trim()) {
      setErr('Name and email are both required.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await api.users.create(token, {
        email: email.trim(),
        display_name: name.trim(),
        role,
      });
      toast.push(
        res.invite_sent
          ? {
              variant: 'success',
              title: `Invite sent to ${res.user.email}`,
              body: 'The link works for 3 days. They pick their own password — you never see it.',
            }
          : {
              variant: 'warning',
              title: `${res.user.display_name} was added, but the invite email did not send`,
              body: 'The account exists. Use Re-invite on their row to try again.',
            },
      );
      await onAdded();
    } catch (e) {
      setErr(addUserErrorMessage(e));
      setBusy(false);
    }
  };

  return (
    <InlineForm
      onSubmit={submit}
      onCancel={onCancel}
      busy={busy}
      err={err}
      // All three columns grow with the viewport — a fixed Role column starved
      // its caption on wide screens while Name/Email ballooned (2026-08-06).
      grid="minmax(160px, 1.1fr) minmax(200px, 1.3fr) minmax(190px, 1fr)"
    >
      {/* The one sentence that used to sit in the section blurb, where it was
          standing furniture. It only matters while you are filling this in. */}
      <p
        style={{
          gridColumn: '1 / -1',
          margin: '0 0 2px',
          fontSize: 12,
          color: 'var(--ink-3)',
          lineHeight: 1.45,
        }}
      >
        Adding someone emails them an invite; they choose their own password.
      </p>
      <Input label="Name" value={name} onChange={setName} />
      <Input label="Work email" value={email} onChange={setEmail} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Select
          label="Role"
          value={role}
          onChange={(v) => setRole(v as UserRole)}
          options={['pm', 'admin']}
          renderOption={(o) => ROLE_LABEL[o as UserRole]}
        />
        {/* Nobody should have to guess what "administrator" grants. */}
        <span style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.35 }}>
          {ROLE_BLURB[role]}
        </span>
      </div>
    </InlineForm>
  );
}

/**
 * Prose for the two roster write failures the PM can actually cause.
 *
 * `ApiCallError`'s `message` is `payload.error`, so without this the inline
 * error row read `duplicate_hj_id` — a machine code, on the one surface where
 * the PM is looking at the field that caused it. Both the create and the rename
 * paths answer with these codes, so both get the sentence.
 */
function rosterSaveErrorMessage(e: unknown): string {
  const code = e instanceof ApiCallError ? e.payload.error : undefined;
  switch (code) {
    case 'duplicate_hj_id':
      return 'That HeavyJob ID is already on this job’s roster.';
    case 'duplicate_asset_id':
      return 'That asset ID is already on this job’s equipment list.';
    default:
      return e instanceof Error ? e.message : 'Save failed';
  }
}

function addUserErrorMessage(e: unknown): string {
  const code = e instanceof ApiCallError ? e.payload.error : undefined;
  switch (code) {
    case 'email_exists':
      return 'Someone already uses that email address on this account.';
    case 'invalid_email':
      return 'That does not look like an email address.';
    case 'invalid_name':
      return 'A name is required.';
    default:
      return e instanceof Error ? e.message : 'Could not add that user.';
  }
}

/**
 * A role change is a PRIVILEGE change, so it is confirmed like every other
 * consequential action here rather than committing on the picker's own change
 * event. Granting administrator should never be one stray click on a dropdown
 * the PM was only reading.
 */
type PendingUserAction =
  | { kind: 'deactivate' }
  | { kind: 'revoke' }
  | { kind: 'role'; next: UserRole }
  | null;

/**
 * How a person gets back in after being signed out — which is NOT "with their
 * password" for someone who has never set one. Four live accounts have no
 * password today, so the unbranched sentence would have been wrong for all of
 * them on day one.
 */
function signBackInPhrase(user: AdminUser): string {
  return user.has_password
    ? 'They can sign back in with their password.'
    : 'They have not set a password yet — they will need their invitation link, or a new one from this tab.';
}

function UserRow({ user, onChanged }: { user: AdminUser; onChanged: () => Promise<unknown> }) {
  const { token } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<PendingUserAction>(null);

  /** One wrapper, so no action can half-fail silently and they all report alike. */
  const run = async (
    label: string,
    fn: () => Promise<unknown>,
    success: { title: string; body: string },
  ) => {
    if (!token) return;
    setBusy(true);
    try {
      await fn();
      toast.push({ variant: 'success', title: success.title, body: success.body });
      await onChanged();
    } catch (e) {
      toast.push({
        variant: 'warning',
        title: `Couldn't ${label}`,
        body: userActionErrorMessage(e),
      });
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  const lockCountdown = unlocksIn(user.locked_until);
  const inviteAge = relativeAge(user.invite_sent_at);
  // A control the server would refuse is disabled here too. DISABLED IS UX,
  // THE 409 IS THE GATE — both, deliberately.
  const roleLocked = user.is_self || user.is_last_admin;
  const roleLockReason = user.is_self
    ? 'You cannot change your own role. Another administrator can.'
    : 'This is the only administrator left — make someone else one first.';

  /**
   * The row's three commands, behind one ⋯ menu. Same handlers, same confirms,
   * same disabled conditions and the same reasons on hover — only the shape
   * changed. Built as data so the disabled ones stay in place instead of the
   * menu changing length from row to row.
   */
  const actions: ActionMenuItem[] = [];
  if (token) {
    if (user.is_locked) {
      actions.push({
        key: 'unlock',
        label: 'Unlock',
        disabled: busy,
        title: lockCountdown
          ? `Locked after too many sign-in attempts. Clears itself in ${lockCountdown}.`
          : 'Clears the sign-in delay and the attempt limits on their email.',
        onSelect: () =>
          void run('unlock that account', () => api.users.unlock(token, user.id), {
            title: `${user.display_name} can sign in again`,
            body: 'The sign-in delay and the attempt limits on their email were cleared.',
          }),
      });
    }
    actions.push({
      key: 'invite',
      label: user.has_password ? 'Send reset link' : 'Re-invite',
      disabled: busy,
      title: user.has_password
        ? 'Emails them a link to set a new password. Any earlier link stops working.'
        : 'Sends the invite again. The earlier link stops working.',
      onSelect: () =>
        void run('send that email', () => api.users.invite(token, user.id), {
          title: `Sent to ${user.email}`,
          body: 'Any earlier link in their inbox is now dead.',
        }),
    });
  }
  actions.push({
    key: 'revoke',
    label: 'Sign out everywhere',
    disabled: busy || user.active_session_count === 0,
    title:
      user.active_session_count === 0
        ? 'Not signed in anywhere right now.'
        : `Signed in on ${user.active_session_count} device(s).`,
    onSelect: () => setConfirming({ kind: 'revoke' }),
  });
  actions.push({
    key: 'deactivate',
    label: 'Remove access',
    danger: true,
    disabled: busy || user.is_self || user.is_last_admin,
    title: user.is_self
      ? 'You cannot remove your own access.'
      : user.is_last_admin
        ? 'This is the only administrator left.'
        : 'Removes their access. Reversible.',
    onSelect: () => setConfirming({ kind: 'deactivate' }),
  });

  return (
    <>
      <tr style={user.is_active ? undefined : { color: 'var(--ink-3)' }}>
        <td>
          {user.display_name}
          {user.is_self && (
            <span style={{ color: 'var(--ink-3)', fontSize: 12 }} title="This is you">
              {' '}
              (you)
            </span>
          )}
          {/* The Status column is gone; this is the one state that still needs
              an eye, because it means the person cannot sign in yet. Nothing
              renders on an ordinary active row. */}
          {user.is_active && !user.has_password && (
            <span
              className="row-tag"
              title={
                inviteAge
                  ? `Invite sent ${inviteAge}. They have not set a password yet.`
                  : 'They have not set a password yet.'
              }
            >
              invite pending
            </span>
          )}
          {user.is_active && user.is_locked && (
            <span
              className="row-tag warn"
              title={
                lockCountdown
                  ? `Too many sign-in attempts. Clears itself in ${lockCountdown}, or use Unlock.`
                  : 'Too many sign-in attempts. Use Unlock, or wait for it to clear.'
              }
            >
              locked
            </span>
          )}
        </td>
        <td style={{ color: 'var(--ink-2)' }}>{user.email}</td>
        <td>
          {/* The title rides the wrapper, not the trigger: a disabled button
              shows no tooltip of its own, and the lock REASON is the whole
              point of the disabled state. */}
          <span
            style={{ display: 'inline-flex' }}
            title={roleLocked ? roleLockReason : ROLE_BLURB[user.role]}
          >
            <Dropdown
              uiSize="sm"
              // Controlled by `user.role`, never by the pending choice: until
              // the confirm lands, the dropdown must keep showing what is
              // actually true. Cancelling leaves the row exactly as it was.
              value={user.role}
              options={ROLE_OPTIONS}
              disabled={busy || roleLocked || !user.is_active}
              aria-label={`Role for ${user.display_name}`}
              onChange={(v) => {
                const next = v as UserRole;
                if (next === user.role) return;
                setConfirming({ kind: 'role', next });
              }}
            />
          </span>
        </td>
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          {user.is_active ? (
            <span style={{ display: 'inline-flex', gap: 4 }}>
              <ActionMenu items={actions} label={`Actions for ${user.display_name}`} />
            </span>
          ) : (
            token && (
              <button
                type="button"
                className="btn-link"
                disabled={busy}
                onClick={() =>
                  void run('restore that account', () => api.users.reactivate(token, user.id), {
                    title: `${user.display_name} has access again`,
                    // Their old sessions were revoked and are not restored, so
                    // they have to get in from scratch — which for someone who
                    // never set a password means the invite, not a password.
                    body: `Their old sessions were not restored. ${signBackInPhrase(user)}`,
                  })
                }
              >
                Restore access
              </button>
            )
          )}
        </td>
      </tr>

      <ConfirmModal
        open={confirming?.kind === 'deactivate'}
        title={`Remove ${user.display_name}'s access?`}
        destructive
        confirmLabel="Remove access"
        busy={busy}
        busyLabel="Removing…"
        body={
          <>
            <p style={{ margin: '0 0 8px' }}>
              {user.display_name} will be signed out of every device immediately and won&rsquo;t be
              able to sign in.
            </p>
            <p style={{ margin: 0, color: 'var(--ink-3)', fontSize: 13 }}>
              Nothing they did is deleted — their rollups, sends and history stay exactly as they
              are, and you can give access back from this tab at any time.
            </p>
          </>
        }
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (!token) return;
          void run('remove that access', () => api.users.deactivate(token, user.id), {
            title: `${user.display_name} no longer has access`,
            body: 'Signed out everywhere. You can restore access from this tab.',
          });
        }}
      />

      <ConfirmModal
        open={confirming?.kind === 'revoke'}
        title={`Sign ${user.display_name} out everywhere?`}
        destructive
        confirmLabel="Sign out everywhere"
        busy={busy}
        busyLabel="Signing out…"
        body={
          <p style={{ margin: 0 }}>
            {user.display_name} will be signed out on every device
            {user.active_session_count > 0 ? ` (${user.active_session_count} right now)` : ''} and
            will have to sign in again. Their access is not removed.{' '}
            {signBackInPhrase(user)}
          </p>
        }
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (!token) return;
          void run('sign that user out', () => api.users.revokeSessions(token, user.id), {
            title: `${user.display_name} was signed out everywhere`,
            body: signBackInPhrase(user),
          });
        }}
      />

      {/* A privilege change gets the same confirm every other consequential
          action gets, with the role's own plain-English description as the
          body — the point is that the admin reads what they are granting. */}
      <ConfirmModal
        open={confirming?.kind === 'role'}
        title={
          confirming?.kind === 'role'
            ? `Make ${user.display_name} a ${ROLE_LABEL[confirming.next].toLowerCase()}?`
            : ''
        }
        destructive={confirming?.kind === 'role' && confirming.next === 'admin'}
        confirmLabel={
          confirming?.kind === 'role' && confirming.next === 'admin'
            ? 'Make administrator'
            : 'Change role'
        }
        busy={busy}
        busyLabel="Saving…"
        body={
          confirming?.kind === 'role' ? (
            <>
              <p style={{ margin: '0 0 8px' }}>{ROLE_BLURB[confirming.next]}</p>
              <p style={{ margin: 0, color: 'var(--ink-3)', fontSize: 13 }}>
                {confirming.next === 'admin'
                  ? `${user.display_name} will be able to add, remove, and re-role people — including you.`
                  : `${user.display_name} keeps all of their billing work and loses only user administration.`}
              </p>
            </>
          ) : null
        }
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (!token || confirming?.kind !== 'role') return;
          const next = confirming.next;
          void run('change that role', () => api.users.patch(token, user.id, { role: next }), {
            title: `${user.display_name} is now a ${ROLE_LABEL[next].toLowerCase()}`,
            body: ROLE_BLURB[next],
          });
        }}
      />
    </>
  );
}

function userActionErrorMessage(e: unknown): string {
  const code = e instanceof ApiCallError ? e.payload.error : undefined;
  switch (code) {
    case 'cannot_change_own_role':
      return 'You cannot change your own role. Ask another administrator to do it.';
    case 'cannot_deactivate_self':
      return 'You cannot remove your own access.';
    case 'last_admin':
      return 'This is the only administrator left. Make someone else an administrator first.';
    case 'user_inactive':
      return 'That account has no access right now. Restore it first.';
    case 'forbidden_not_admin':
      return 'Only an administrator can do this.';
    default:
      return e instanceof Error ? e.message : 'Please try again.';
  }
}
